import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { PlatformLayout } from "./platform-layout";
import { apiRequest, queryClient as globalQueryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { PlatformGmailConnector } from "@/components/platform/platform-gmail-connector";
import { InfraStorefront, BuyDialog, type InfraPackage } from "@/components/infra-storefront";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Mail, Loader2, Save, ShieldCheck, Link as LinkIcon, Percent, CheckCircle2, Clock, Inbox, Server, Gauge, Activity, AlertTriangle, ShieldAlert, RefreshCw, Plug, Search, SlidersHorizontal, ExternalLink, Globe, MailCheck, Plus, Trash2, Building2, Zap, ArrowRight, Key, Download, Filter, Flame, ThermometerSun, ChevronRight, Layers, Wifi, BarChart3, Package, Settings2 } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { PlatformOfficeHoursPanel } from "@/components/platform/platform-office-hours-panel";
import { GmailReadContent } from "./platform-gmail-read";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// ─── Cold Domain Requests (moved from Sending Infra) ──────────────────────────

interface ColdDomainRequestRow {
  id: string;
  workspaceId: string;
  workspaceName: string | null;
  requestedDomains: number;
  requestedMailboxes: number;
  estimatedMonthlyCents: number;
  notes: string | null;
  status: "pending" | "handled";
  createdAt: string;
  handledAt: string | null;
}

function ColdDomainRequestsCard() {
  const { toast } = useToast();
  const { data: requests = [], isLoading } = useQuery<ColdDomainRequestRow[]>({
    queryKey: ["/api/platform/infra/cold-requests"],
  });

  const handleMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: "pending" | "handled" }) => {
      const res = await apiRequest("POST", `/api/platform/infra/cold-requests/${id}/handle`, { status });
      return res.json();
    },
    onSuccess: () => {
      globalQueryClient.invalidateQueries({ queryKey: ["/api/platform/infra/cold-requests"] });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err?.message, variant: "destructive" });
    },
  });

  const pending = requests.filter((r) => r.status === "pending");
  const handled = requests.filter((r) => r.status === "handled");

  const fmtUsd = (cents: number) =>
    `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const renderRow = (r: ColdDomainRequestRow) => (
    <div
      key={r.id}
      className="flex items-start justify-between gap-3 rounded-md border p-3"
      data-testid={`row-cold-request-${r.id}`}
    >
      <div className="space-y-1 min-w-0">
        <div className="flex items-center gap-2 text-sm font-medium">
          <span className="truncate" data-testid={`text-cold-request-workspace-${r.id}`}>
            {r.workspaceName || r.workspaceId}
          </span>
          {r.status === "pending" ? (
            <Badge variant="secondary" className="shrink-0">
              <Clock className="mr-1 h-3 w-3" /> Pending
            </Badge>
          ) : (
            <Badge variant="outline" className="shrink-0">
              <CheckCircle2 className="mr-1 h-3 w-3 text-green-600" /> Handled
            </Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          {r.requestedDomains} domain(s) · {r.requestedMailboxes} mailbox(es) per domain · est.{" "}
          {fmtUsd(r.estimatedMonthlyCents)}/mo
        </p>
        {r.notes && (
          <p className="text-xs text-muted-foreground italic">"{r.notes}"</p>
        )}
        <p className="text-[11px] text-muted-foreground">
          {new Date(r.createdAt).toLocaleString()}
        </p>
      </div>
      <div className="shrink-0">
        {r.status === "pending" ? (
          <Button
            size="sm"
            onClick={() => handleMutation.mutate({ id: r.id, status: "handled" })}
            disabled={handleMutation.isPending}
            data-testid={`button-handle-cold-request-${r.id}`}
          >
            {handleMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <CheckCircle2 className="mr-2 h-4 w-4" />
            )}
            Mark handled
          </Button>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => handleMutation.mutate({ id: r.id, status: "pending" })}
            disabled={handleMutation.isPending}
            data-testid={`button-reopen-cold-request-${r.id}`}
          >
            Reopen
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <Card data-testid="card-cold-requests">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Mail className="h-4 w-4" /> Cold Domain Requests
          {pending.length > 0 && (
            <Badge variant="destructive" data-testid="badge-cold-pending-count">{pending.length}</Badge>
          )}
        </CardTitle>
        <CardDescription>
          Beta manual-fulfillment queue. Tenants request cold-sending domains/mailboxes here —
          provision them in the Maildoso dashboard, then mark the request handled. This is separate
          from the auto-provision ledger below.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : requests.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="text-cold-requests-empty">
            No cold-domain requests yet.
          </p>
        ) : (
          <>
            {pending.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground">Pending</p>
                {pending.map(renderRow)}
              </div>
            )}
            {handled.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground">Handled</p>
                {handled.map(renderRow)}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Platform Cold Email Card ─────────────────────────────────────────────────

interface PlatformColdPurchaseItem {
  id: string;
  itemType: "domain" | "mailbox";
  identifier: string;
  status: string;
  smtpHost: string | null;
  smtpUser: string | null;
}

interface PlatformColdPurchase {
  id: string;
  domainsCount: number;
  mailboxesCount: number;
  provisioningStatus: string;
  notes: string | null;
  createdAt: string;
  items: PlatformColdPurchaseItem[];
}

interface PlatformColdStatus {
  purchases: PlatformColdPurchase[];
  fromName: string;
  fromEmail: string;
  isEnabled: boolean;
  liveMailboxCount: number;
}

interface WorkspaceItem {
  id: string;
  name: string;
}

// ─── Seasoning Gate Status Card (migration 0191) ──────────────────────────────

interface SeasoningMailbox {
  id: string;
  identifier: string;
  workspaceId: string;
  probesSent: number;
  probesNeeded: number;
  remaining: number;
  etaDays: number;
  dnsVerified: boolean;
  domainIdentifier: string;
  seasoningStartedAt: string | null;
  coldCapable: boolean;
  tests: Array<{ id: string; testEmailAddress: string; sentAt: string; result: string }>;
}

function SeasoningStatusCard() {
  const { toast } = useToast();
  const qc = useQueryClient();

  const { data: items = [], isLoading } = useQuery<SeasoningMailbox[]>({
    queryKey: ["/api/platform/infra/seasoning"],
    refetchInterval: 60_000,
  });

  const markCapableMutation = useMutation({
    mutationFn: async ({ id, capable }: { id: string; capable: boolean }) => {
      const res = await apiRequest("POST", `/api/platform/infra/items/${id}/mark-cold-capable`, { capable });
      return res.json();
    },
    onSuccess: (_data, vars) => {
      toast({
        title: vars.capable ? "Mailbox marked ready" : "Mailbox returned to seasoning",
        description: vars.capable
          ? "The mailbox has been admitted to the cold-send relay pool."
          : "The mailbox has been moved back to seasoning state.",
      });
      qc.invalidateQueries({ queryKey: ["/api/platform/infra/seasoning"] });
      qc.invalidateQueries({ queryKey: ["/api/platform/infra/items"] });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err?.message ?? "Failed to update", variant: "destructive" });
    },
  });

  if (isLoading) {
    return (
      <Card data-testid="card-seasoning-loading">
        <CardContent className="py-6 flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">Loading warming status…</span>
        </CardContent>
      </Card>
    );
  }

  if (items.length === 0) {
    return (
      <Card data-testid="card-seasoning-empty">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Flame className="h-4 w-4 text-orange-500" />
            Mailbox Warming
          </CardTitle>
          <CardDescription>
            New managed mailboxes must pass inbox-placement tests before cold sends are
            enabled. All current mailboxes are cold-capable — no warming in progress.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="text-seasoning-all-ready">
            <CheckCircle2 className="h-4 w-4 text-green-500" />
            All provisioned mailboxes are cleared for cold sending.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="card-seasoning-status">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Flame className="h-4 w-4 text-orange-500" />
          Mailbox Warming
          <Badge variant="secondary" data-testid="badge-seasoning-count">
            {items.length} warming
          </Badge>
        </CardTitle>
        <CardDescription>
          These mailboxes are in the seasoning phase. The placement-test scheduler sends
          probe emails every 24 h and graduates each mailbox after{" "}
          <strong>{items[0]?.probesNeeded ?? 5}</strong> successful probes + DNS
          verification. Use <em>Mark ready</em> to override if you've verified externally.
        </CardDescription>
      </CardHeader>

      {/* Setup prompt — shown when PLACEMENT_TEST_EMAILS is not configured */}
      <CardContent className="pb-0">
        <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-4 mb-4 space-y-2" data-testid="banner-seasoning-setup">
          <p className="text-sm font-medium flex items-center gap-1.5 text-amber-800 dark:text-amber-300">
            <ThermometerSun className="h-4 w-4" />
            Configure placement test recipients
          </p>
          <p className="text-xs text-amber-700 dark:text-amber-400">
            Set the <code className="bg-amber-100 dark:bg-amber-900 px-1 rounded">PLACEMENT_TEST_EMAILS</code> environment
            variable (comma-separated inbox addresses you control) so the scheduler can
            automatically send probe emails and track inbox placement. Without it, mailboxes
            can only be graduated via the <em>Mark ready</em> override below.
          </p>
        </div>
      </CardContent>

      <CardContent className="space-y-4">
        {items.map((item) => {
          const pct = Math.round((item.probesSent / Math.max(item.probesNeeded, 1)) * 100);
          const startedDate = item.seasoningStartedAt
            ? new Date(item.seasoningStartedAt).toLocaleDateString()
            : "—";
          const isPending = markCapableMutation.isPending && (markCapableMutation.variables as any)?.id === item.id;

          return (
            <div
              key={item.id}
              className="rounded-lg border bg-card p-4 space-y-3"
              data-testid={`card-seasoning-item-${item.id}`}
            >
              {/* Header row */}
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate" data-testid={`text-seasoning-address-${item.id}`}>
                    {item.identifier}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Domain: <span className="font-mono">{item.domainIdentifier}</span>
                    {" · "}Started {startedDate}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => markCapableMutation.mutate({ id: item.id, capable: true })}
                  disabled={isPending}
                  data-testid={`button-mark-capable-${item.id}`}
                >
                  {isPending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <CheckCircle2 className="h-3 w-3 mr-1" />}
                  Mark ready
                </Button>
              </div>

              {/* Progress bar */}
              <div className="space-y-1">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Placement probes</span>
                  <span data-testid={`text-seasoning-probes-${item.id}`}>
                    {item.probesSent} / {item.probesNeeded}
                  </span>
                </div>
                <Progress value={pct} className="h-2" data-testid={`progress-seasoning-${item.id}`} />
              </div>

              {/* Status chips */}
              <div className="flex flex-wrap gap-2">
                <Badge
                  variant={item.dnsVerified ? "default" : "secondary"}
                  className={item.dnsVerified ? "bg-green-600" : ""}
                  data-testid={`badge-dns-${item.id}`}
                >
                  {item.dnsVerified ? (
                    <><CheckCircle2 className="h-3 w-3 mr-1" />DNS verified</>
                  ) : (
                    <><AlertTriangle className="h-3 w-3 mr-1" />DNS unverified</>
                  )}
                </Badge>
                <Badge variant="outline" data-testid={`badge-eta-${item.id}`}>
                  <Clock className="h-3 w-3 mr-1" />
                  {item.remaining === 0 ? "Ready to graduate" : `~${item.etaDays}d remaining`}
                </Badge>
              </div>

              {/* Recent tests */}
              {item.tests.length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-muted-foreground hover:text-foreground select-none">
                    Recent probes ({item.tests.length})
                  </summary>
                  <ul className="mt-2 space-y-1 pl-1">
                    {item.tests.slice(0, 5).map((t) => (
                      <li key={t.id} className="flex items-center gap-2 text-muted-foreground" data-testid={`row-probe-${t.id}`}>
                        <span className={cn(
                          "w-1.5 h-1.5 rounded-full shrink-0",
                          t.result === "inbox" ? "bg-green-500" :
                          t.result === "spam" ? "bg-red-500" : "bg-amber-400"
                        )} />
                        <span className="font-mono truncate max-w-[14rem]">{t.testEmailAddress}</span>
                        <span className="shrink-0">{new Date(t.sentAt).toLocaleDateString()}</span>
                        <Badge variant="outline" className="text-[10px] px-1 py-0">{t.result}</Badge>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

// Mirror of the server-side MAILBOX_LOCAL_PARTS base names in
// server/lib/infra-provider/maildoso.ts. Used only to PREVIEW the local-parts
// that will be generated (Maildoso appends a random suffix per mailbox for
// uniqueness, so these are the human-readable bases, not the final addresses).
const PLATFORM_MAILBOX_LOCAL_PARTS = [
  "outreach",
  "hello",
  "team",
  "hi",
  "contact",
  "sales",
];

/** Build a short preview of the local-parts that will be created for a count. */
function previewLocalParts(count: number): string[] {
  const parts: string[] = [];
  for (let j = 0; j < count; j++) {
    const base = PLATFORM_MAILBOX_LOCAL_PARTS[j % PLATFORM_MAILBOX_LOCAL_PARTS.length];
    const cycle = Math.floor(j / PLATFORM_MAILBOX_LOCAL_PARTS.length);
    parts.push(`${base}${cycle > 0 ? cycle + 1 : ""}`);
  }
  return parts;
}

function PlatformColdEmailCard() {
  const { toast } = useToast();
  const [showSetup, setShowSetup] = useState(false);
  const [showBuy, setShowBuy] = useState(false);
  const [domainCount, setDomainCount] = useState(3);
  const [mailboxCount, setMailboxCount] = useState(12);
  const [fromNameLocal, setFromNameLocal] = useState("");
  const [reassignPurchaseId, setReassignPurchaseId] = useState<string | null>(null);
  const [targetWsId, setTargetWsId] = useState("");

  const { data: status, isLoading } = useQuery<PlatformColdStatus>({
    queryKey: ["/api/platform/cold-email/status"],
  });

  // Live Maildoso capacity so the count picker can validate the requested
  // mailbox total against remaining licensed headroom before provisioning.
  const { data: capacityRec } = useQuery<CapacityRecommendationResponse>({
    queryKey: ["/api/platform/infra/capacity-recommendation"],
    queryFn: async () =>
      (await apiRequest("GET", "/api/platform/infra/capacity-recommendation?additionalDemand=0")).json(),
    enabled: showSetup,
  });
  const mailboxesRemaining = capacityRec?.capacity?.mailboxesRemaining ?? null;
  const capacityKnown = capacityRec?.capacityKnown ?? false;
  const overCapacity = capacityKnown && mailboxesRemaining != null && mailboxCount > mailboxesRemaining;

  useEffect(() => {
    if (status?.fromName && !fromNameLocal) setFromNameLocal(status.fromName);
  }, [status?.fromName]);

  const { data: workspacesList } = useQuery<WorkspaceItem[]>({
    queryKey: ["/api/platform/workspaces"],
    enabled: !!reassignPurchaseId,
  });

  const fromNameMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PUT", "/api/platform/email-settings/cold", {
        fromName: fromNameLocal,
      });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "From name saved" });
      globalQueryClient.invalidateQueries({ queryKey: ["/api/platform/cold-email/status"] });
    },
    onError: (err: any) =>
      toast({ title: "Error", description: err?.message, variant: "destructive" }),
  });

  const cancelMutation = useMutation({
    mutationFn: async (purchaseId: string) => {
      const res = await apiRequest("POST", `/api/platform/infra/purchases/${purchaseId}/cancel`);
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Purchase cancelled" });
      globalQueryClient.invalidateQueries({ queryKey: ["/api/platform/cold-email/status"] });
    },
    onError: (err: any) =>
      toast({ title: "Cancel failed", description: err?.message, variant: "destructive" }),
  });

  const reassignMutation = useMutation({
    mutationFn: async ({
      purchaseId,
      workspaceId,
    }: {
      purchaseId: string;
      workspaceId: string;
    }) => {
      const res = await apiRequest(
        "POST",
        `/api/platform/cold-email/purchases/${purchaseId}/reassign`,
        { targetWorkspaceId: workspaceId },
      );
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Reassigned to tenant" });
      setReassignPurchaseId(null);
      setTargetWsId("");
      globalQueryClient.invalidateQueries({ queryKey: ["/api/platform/cold-email/status"] });
    },
    onError: (err: any) =>
      toast({ title: "Reassign failed", description: err?.message, variant: "destructive" }),
  });

  const syntheticPkg: InfraPackage = {
    id: "__platform_cold__",
    name: "Platform Cold Email",
    description: "Managed sending infrastructure for platform cold outreach",
    purchaseType: "buy",
    domainsCount: domainCount,
    mailboxesCount: mailboxCount,
    wholesaleCentsPerDomain: 0,
    wholesaleCentsPerMailbox: 0,
    retailCentsPerDomain: 0,
    retailCentsPerMailbox: 0,
    domainBillingCycle: "annual",
    mailboxBillingCycle: "monthly",
    billingMode: "flat_bundle",
    flatRateMonthlyWholesaleCents: 0,
    flatRateMonthlyRetailCents: 0,
    isActive: true,
    sortOrder: 0,
  };

  const purchases = status?.purchases ?? [];
  const totalLive = status?.liveMailboxCount ?? 0;

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                <Zap className="w-4 h-4 text-primary" />
              </div>
              <div>
                <CardTitle className="text-base">Platform Cold Email</CardTitle>
                <CardDescription>
                  Provisioned Maildoso domains &amp; mailboxes for platform lead-gen and AI Agent
                  campaigns. The send path reads these automatically — no manual SMTP env vars
                  needed.
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0 pt-0.5">
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : totalLive > 0 ? (
                <Badge className="bg-green-600 hover:bg-green-700 text-white">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  {totalLive} live mailbox{totalLive !== 1 ? "es" : ""}
                </Badge>
              ) : (
                <Badge variant="outline" className="text-muted-foreground">
                  Not enabled
                </Badge>
              )}
              <Button
                size="sm"
                variant={totalLive > 0 ? "outline" : "default"}
                onClick={() => setShowSetup(true)}
                data-testid="button-enable-platform-cold"
              >
                <Plus className="h-3.5 w-3.5 mr-1.5" />
                {totalLive > 0 ? "Add more" : "Enable cold email"}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* From-name */}
          <div className="flex items-end gap-2 max-w-sm">
            <div className="flex-1 space-y-1.5">
              <Label htmlFor="platform-cold-from-name">From name</Label>
              <Input
                id="platform-cold-from-name"
                placeholder="Clarik Outreach"
                value={fromNameLocal}
                onChange={(e) => setFromNameLocal(e.target.value)}
                data-testid="input-platform-cold-from-name"
              />
            </div>
            <Button
              size="sm"
              variant="outline"
              disabled={fromNameMutation.isPending}
              onClick={() => fromNameMutation.mutate()}
              data-testid="button-save-platform-cold-from-name"
            >
              {fromNameMutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Save className="h-3.5 w-3.5" />
              )}
            </Button>
          </div>

          {/* Purchases list */}
          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : purchases.length === 0 ? (
            <div className="rounded-md border border-dashed border-border p-4 text-sm text-muted-foreground text-center">
              No platform cold mailboxes provisioned yet. Click "Enable cold email" to get started.
            </div>
          ) : (
            <div className="space-y-3">
              {purchases.map((purchase) => {
                const domains = purchase.items.filter((i) => i.itemType === "domain");
                const mailboxes = purchase.items.filter((i) => i.itemType === "mailbox");
                const isLive = purchase.provisioningStatus === "live";
                return (
                  <div
                    key={purchase.id}
                    className="rounded-md border border-border p-3 space-y-2"
                    data-testid={`card-platform-cold-purchase-${purchase.id}`}
                  >
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-2">
                        <Badge
                          className={
                            isLive
                              ? "bg-green-600 hover:bg-green-700 text-white text-[11px] h-5"
                              : "text-[11px] h-5"
                          }
                          variant={isLive ? "default" : "outline"}
                        >
                          {purchase.provisioningStatus}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {purchase.domainsCount}d / {purchase.mailboxesCount}mx ·{" "}
                          {new Date(purchase.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={() => {
                            setReassignPurchaseId(purchase.id);
                            setTargetWsId("");
                          }}
                          data-testid={`button-reassign-purchase-${purchase.id}`}
                        >
                          <Building2 className="h-3 w-3 mr-1" /> Reassign to tenant
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs text-destructive hover:text-destructive"
                          disabled={cancelMutation.isPending}
                          onClick={() => cancelMutation.mutate(purchase.id)}
                          data-testid={`button-cancel-purchase-${purchase.id}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    {domains.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {domains.map((d) => (
                          <span
                            key={d.id}
                            className="flex items-center gap-1 text-[11px] bg-muted rounded px-1.5 py-0.5"
                          >
                            <Globe className="h-2.5 w-2.5 text-muted-foreground" />
                            {d.identifier || "—"}
                          </span>
                        ))}
                      </div>
                    )}
                    {mailboxes.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {mailboxes.map((m) => (
                          <span
                            key={m.id}
                            className="flex items-center gap-1 text-[11px] bg-muted rounded px-1.5 py-0.5"
                          >
                            <MailCheck className="h-2.5 w-2.5 text-muted-foreground" />
                            {m.identifier || m.smtpUser || "—"}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Step 1: Count picker + cost awareness */}
      {showSetup && !showBuy && (
        <Dialog open onOpenChange={() => setShowSetup(false)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Configure Platform Cold Email</DialogTitle>
              <DialogDescription>
                Choose how many domains and mailboxes to provision via Maildoso. Domains are
                registered and mailboxes created automatically.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-1">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="plat-domain-count">Domains</Label>
                  <Input
                    id="plat-domain-count"
                    type="number"
                    min={1}
                    max={20}
                    value={domainCount}
                    onChange={(e) => {
                      const newVal = Math.max(1, parseInt(e.target.value) || 1);
                      setDomainCount(newVal);
                      setMailboxCount(newVal * 4);
                    }}
                    data-testid="input-platform-cold-domain-count"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="plat-mailbox-count">Mailboxes (total)</Label>
                  <Input
                    id="plat-mailbox-count"
                    type="number"
                    min={1}
                    max={100}
                    value={mailboxCount}
                    onChange={(e) =>
                      setMailboxCount(Math.max(1, parseInt(e.target.value) || 1))
                    }
                    data-testid="input-platform-cold-mailbox-count"
                  />
                </div>
              </div>
              {/* Local-part preview — the base names Maildoso will create for
                  each mailbox (a random suffix is appended per address). */}
              <div className="space-y-1.5" data-testid="section-localpart-preview">
                <Label>Mailbox local-parts</Label>
                <div className="flex flex-wrap gap-1.5">
                  {previewLocalParts(Math.min(mailboxCount, 12)).map((lp, i) => (
                    <span
                      key={`${lp}-${i}`}
                      className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono text-muted-foreground"
                      data-testid={`chip-localpart-${i}`}
                    >
                      {lp}@…
                    </span>
                  ))}
                  {mailboxCount > 12 && (
                    <span className="text-xs text-muted-foreground">
                      +{mailboxCount - 12} more
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Each address gets a unique random suffix, distributed evenly across the{" "}
                  {domainCount} domain{domainCount === 1 ? "" : "s"}.
                </p>
              </div>
              {/* Capacity validation against remaining licensed Maildoso headroom. */}
              {overCapacity && (
                <div
                  className="rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3 flex gap-2.5"
                  data-testid="alert-over-capacity"
                >
                  <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
                  <div className="text-xs text-red-800 dark:text-red-300 space-y-0.5">
                    <div className="font-medium">Exceeds licensed capacity</div>
                    <div>
                      You requested {mailboxCount} mailbox{mailboxCount === 1 ? "" : "es"} but only{" "}
                      {mailboxesRemaining} slot{mailboxesRemaining === 1 ? "" : "s"} remain. Raise the
                      Maildoso license tier (dashboard-only) or lower the count.
                    </div>
                  </div>
                </div>
              )}
              {capacityKnown && !overCapacity && mailboxesRemaining != null && (
                <p className="text-[11px] text-muted-foreground" data-testid="text-capacity-remaining">
                  {mailboxesRemaining} licensed mailbox slot{mailboxesRemaining === 1 ? "" : "s"} remaining.
                </p>
              )}
              <div className="rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3 flex gap-2.5">
                <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <div className="text-xs text-amber-800 dark:text-amber-300 space-y-0.5">
                  <div className="font-medium">Real Maildoso cost</div>
                  <div>
                    Provisioning registers actual domains and creates mailboxes on Maildoso. This
                    will be billed to the platform&apos;s Maildoso account. No charge to any tenant
                    workspace.
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSetup(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => setShowBuy(true)}
                disabled={overCapacity}
                data-testid="button-choose-domains"
              >
                Choose Domains <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Step 2: BuyDialog — domain selection + provision */}
      {showBuy && (
        <BuyDialog
          pkg={syntheticPkg}
          mode="buy"
          surface="platform"
          workspaceIdOverride="__platform_cold__"
          onClose={() => {
            setShowBuy(false);
            setShowSetup(false);
            globalQueryClient.invalidateQueries({
              queryKey: ["/api/platform/cold-email/status"],
            });
          }}
        />
      )}

      {/* Reassign-to-tenant dialog */}
      {reassignPurchaseId && (
        <Dialog open onOpenChange={() => setReassignPurchaseId(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Reassign to Tenant</DialogTitle>
              <DialogDescription>
                This moves the entire purchase (all its domains and mailboxes) to the selected
                tenant workspace.
              </DialogDescription>
            </DialogHeader>
            <div className="py-2 space-y-2">
              <Label>Target workspace</Label>
              <Select value={targetWsId} onValueChange={setTargetWsId}>
                <SelectTrigger data-testid="select-reassign-target">
                  <SelectValue placeholder="Select workspace…" />
                </SelectTrigger>
                <SelectContent>
                  {(workspacesList ?? []).map((ws) => (
                    <SelectItem key={ws.id} value={ws.id}>
                      {ws.name || ws.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReassignPurchaseId(null)}>
                Cancel
              </Button>
              <Button
                disabled={!targetWsId || reassignMutation.isPending}
                onClick={() =>
                  reassignMutation.mutate({
                    purchaseId: reassignPurchaseId!,
                    workspaceId: targetWsId,
                  })
                }
                data-testid="button-confirm-reassign"
              >
                {reassignMutation.isPending && (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                )}
                Reassign
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

// ─── Markup Configuration (moved from Sending Infra) ──────────────────────────

function MarkupConfigCard() {
  const { toast } = useToast();
  const { data: config, isLoading } = useQuery<{ defaultMarkupPct: number }>({
    queryKey: ["/api/platform/infra/markup"],
  });
  const [markupPct, setMarkupPct] = useState<string>("");

  const saveMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PUT", "/api/platform/infra/markup", {
        markupPct: Number(markupPct),
      });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Markup saved" });
      globalQueryClient.invalidateQueries({ queryKey: ["/api/platform/infra/markup"] });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err?.message, variant: "destructive" });
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Percent className="h-4 w-4" /> Markup Configuration
        </CardTitle>
        <CardDescription>
          Global default markup applied when calculating retail prices from wholesale costs.
          This is informational — retail prices on packages are set explicitly.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : (
          <div className="flex items-end gap-3">
            <div className="space-y-1">
              <Label htmlFor="markup-pct">Default Markup %</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="markup-pct"
                  data-testid="input-markup-pct"
                  type="number"
                  min={0}
                  max={1000}
                  className="w-32"
                  placeholder={String(config?.defaultMarkupPct ?? 40)}
                  value={markupPct}
                  onChange={(e) => setMarkupPct(e.target.value)}
                />
                <span className="text-sm text-muted-foreground">%</span>
              </div>
            </div>
            <Button
              size="sm"
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending || markupPct === ""}
              data-testid="button-save-markup"
            >
              {saveMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              Save
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Capacity buffer, alerts & recommended buy (Task #396) ────────────────────

interface CapacityConfigData {
  bufferMailboxes: number;
  alertThresholdMailboxes: number;
  alertRecipient: string;
  purchaseUrl: string;
  pricing: {
    mailboxBands: { minMailboxes: number; centsPerMailboxMonthly: number }[];
    domainCentsPerYear: number;
    bundles: {
      id: string;
      label: string;
      mailboxes: number;
      freeDomains: number;
      priceCents: number;
      intervalMonths: number;
    }[];
  };
}

interface BundleOptionData {
  bundleId: string;
  label: string;
  unitsToBuy: number;
  mailboxesAdded: number;
  freeDomainsIncluded: number;
  totalCents: number;
  monthlyCents: number;
  effectiveCentsPerMailboxMonthly: number;
}

interface CapacityRecommendationData {
  withinBuffer: boolean;
  capacityUnknown: boolean;
  neededMailboxes: number;
  projectedRemaining: number | null;
  recommended: BundleOptionData | null;
  options: BundleOptionData[];
  alaCarteMonthlyCents: number | null;
  monthlySavingsVsAlaCarte: number;
  note: string;
}

interface CapacityRecommendationResponse {
  capacity: {
    mailboxesUsed: number;
    mailboxesCapacity: number | null;
    mailboxesRemaining: number | null;
  } | null;
  capacityKnown: boolean;
  config: CapacityConfigData;
  recommendation: CapacityRecommendationData;
  supportTicketDraft: string;
}

function usd(cents: number | null | undefined): string {
  if (cents == null) return "—";
  return `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function CapacityBufferCard() {
  const { toast } = useToast();
  const { data: config, isLoading: configLoading } = useQuery<CapacityConfigData>({
    queryKey: ["/api/platform/infra/capacity-config"],
  });
  const [buffer, setBuffer] = useState<string>("");
  const [alertThreshold, setAlertThreshold] = useState<string>("");
  const [recipient, setRecipient] = useState<string>("");
  const [purchaseUrl, setPurchaseUrl] = useState<string>("");
  const [pricingJson, setPricingJson] = useState<string>("");
  const [additionalDemand, setAdditionalDemand] = useState<string>("0");

  const {
    data: rec,
    isLoading: recLoading,
    refetch: refetchRec,
    isFetching: recFetching,
  } = useQuery<CapacityRecommendationResponse>({
    queryKey: ["/api/platform/infra/capacity-recommendation"],
    queryFn: async () =>
      (
        await apiRequest(
          "GET",
          `/api/platform/infra/capacity-recommendation?additionalDemand=${Number(additionalDemand) || 0}`,
        )
      ).json(),
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = {};
      if (buffer !== "") payload.bufferMailboxes = Number(buffer);
      if (alertThreshold !== "") payload.alertThresholdMailboxes = Number(alertThreshold);
      if (recipient !== "") payload.alertRecipient = recipient.trim();
      if (purchaseUrl !== "") payload.purchaseUrl = purchaseUrl.trim();
      if (pricingJson.trim() !== "") {
        let parsed: unknown;
        try {
          parsed = JSON.parse(pricingJson);
        } catch {
          throw new Error("Pricing JSON is not valid JSON.");
        }
        payload.pricing = parsed;
      }
      return (await apiRequest("PUT", "/api/platform/infra/capacity-config", payload)).json();
    },
    onSuccess: () => {
      toast({ title: "Capacity config saved" });
      setBuffer("");
      setRecipient("");
      setPurchaseUrl("");
      setPricingJson("");
      globalQueryClient.invalidateQueries({ queryKey: ["/api/platform/infra/capacity-config"] });
      globalQueryClient.invalidateQueries({ queryKey: ["/api/platform/infra/capacity-recommendation"] });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err?.message, variant: "destructive" });
    },
  });

  const copyTicket = async () => {
    if (!rec?.supportTicketDraft) return;
    try {
      await navigator.clipboard.writeText(rec.supportTicketDraft);
      toast({ title: "Support ticket copied" });
    } catch {
      toast({ title: "Copy failed", description: "Select and copy manually.", variant: "destructive" });
    }
  };

  const recommendation = rec?.recommendation;

  return (
    <Card data-testid="card-capacity-buffer">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <SlidersHorizontal className="h-4 w-4" /> Capacity buffer &amp; alerts
        </CardTitle>
        <CardDescription>
          Keep spare licensed mailbox slots ahead of demand. When headroom drops below the
          buffer we email the alert recipient a recommended buy plus a copy-paste support ticket
          (the mailbox license tier can only be raised from the Maildoso dashboard — never via API).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {configLoading ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : (
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1">
              <Label htmlFor="cap-buffer">Target buffer (mailbox slots)</Label>
              <Input
                id="cap-buffer"
                data-testid="input-capacity-buffer"
                type="number"
                min={0}
                placeholder={String(config?.bufferMailboxes ?? 8)}
                value={buffer}
                onChange={(e) => setBuffer(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="cap-alert-threshold">Low-buffer alert at (slots)</Label>
              <Input
                id="cap-alert-threshold"
                data-testid="input-capacity-alert-threshold"
                type="number"
                min={0}
                placeholder={String(config?.alertThresholdMailboxes ?? config?.bufferMailboxes ?? 8)}
                value={alertThreshold}
                onChange={(e) => setAlertThreshold(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="cap-recipient">Alert recipient</Label>
              <Input
                id="cap-recipient"
                data-testid="input-capacity-recipient"
                type="email"
                placeholder={config?.alertRecipient ?? "admin@clarik.io"}
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="cap-url">Purchase / upgrade URL</Label>
              <Input
                id="cap-url"
                data-testid="input-capacity-url"
                placeholder={config?.purchaseUrl ?? "https://app.maildoso.com/billing"}
                value={purchaseUrl}
                onChange={(e) => setPurchaseUrl(e.target.value)}
              />
            </div>
          </div>
        )}

        <details className="rounded-md border p-3">
          <summary className="cursor-pointer text-sm font-medium">
            Advanced: pricing tiers &amp; bundles (JSON)
          </summary>
          <p className="mt-2 text-xs text-muted-foreground">
            Editable pricing used by the optimizer (mailbox bands, domain cost, license bundles).
            Leave blank to keep the current values. Current:
          </p>
          <pre className="mt-2 max-h-48 overflow-auto rounded bg-muted p-2 text-xs" data-testid="text-current-pricing">
            {config ? JSON.stringify(config.pricing, null, 2) : ""}
          </pre>
          <textarea
            data-testid="input-capacity-pricing"
            className="mt-2 w-full rounded-md border bg-background p-2 font-mono text-xs"
            rows={8}
            placeholder='{"mailboxBands":[...],"domainCentsPerYear":1200,"bundles":[...]}'
            value={pricingJson}
            onChange={(e) => setPricingJson(e.target.value)}
          />
        </details>

        <div>
          <Button
            size="sm"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
            data-testid="button-save-capacity-config"
          >
            {saveMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Save config
          </Button>
        </div>

        <Separator />

        {/* Recommended buy */}
        <div className="space-y-3">
          <div className="flex items-end justify-between gap-3">
            <div className="space-y-1">
              <Label htmlFor="cap-demand">Plan for extra demand (mailboxes)</Label>
              <Input
                id="cap-demand"
                data-testid="input-capacity-demand"
                type="number"
                min={0}
                className="w-40"
                value={additionalDemand}
                onChange={(e) => setAdditionalDemand(e.target.value)}
              />
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => refetchRec()}
              disabled={recFetching}
              data-testid="button-recompute-recommendation"
            >
              {recFetching ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Recompute
            </Button>
          </div>

          {recLoading ? (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          ) : !rec ? (
            <p className="text-sm text-muted-foreground">No recommendation available.</p>
          ) : (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2 text-sm" data-testid="text-capacity-summary">
                {rec.capacityKnown && rec.capacity ? (
                  <>
                    <Badge variant="outline">Capacity: {rec.capacity.mailboxesCapacity ?? "—"}</Badge>
                    <Badge variant="outline">Used: {rec.capacity.mailboxesUsed}</Badge>
                    <Badge variant="outline">Remaining: {rec.capacity.mailboxesRemaining}</Badge>
                  </>
                ) : (
                  <Badge variant="destructive" data-testid="badge-capacity-unknown">
                    Provider capacity unknown
                  </Badge>
                )}
                {recommendation?.withinBuffer ? (
                  <Badge className="bg-green-600 hover:bg-green-600">Within buffer</Badge>
                ) : (
                  <Badge variant="destructive">
                    Need {recommendation?.neededMailboxes} more slot(s)
                  </Badge>
                )}
              </div>

              {recommendation?.recommended ? (
                <div className="rounded-md border p-3 text-sm" data-testid="text-recommended-buy">
                  <p className="font-medium">Recommended: {recommendation.recommended.label} × {recommendation.recommended.unitsToBuy}</p>
                  <p className="text-muted-foreground">
                    +{recommendation.recommended.mailboxesAdded} mailbox slots,{" "}
                    {recommendation.recommended.freeDomainsIncluded} free domains —{" "}
                    {usd(recommendation.recommended.totalCents)}/period (
                    {usd(recommendation.recommended.monthlyCents)}/mo,{" "}
                    {usd(recommendation.recommended.effectiveCentsPerMailboxMonthly)}/mailbox/mo)
                  </p>
                  {recommendation.alaCarteMonthlyCents != null && (
                    <p className="text-muted-foreground">
                      À-la-carte equivalent: {usd(recommendation.alaCarteMonthlyCents)}/mo — recommended saves{" "}
                      {usd(recommendation.monthlySavingsVsAlaCarte)}/mo
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground" data-testid="text-no-buy-needed">
                  {recommendation?.note ?? "No purchase needed."}
                </p>
              )}

              {/* Support ticket draft */}
              {rec.supportTicketDraft && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Support ticket draft (dashboard-only license upgrade)</Label>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={copyTicket}
                      data-testid="button-copy-ticket"
                    >
                      Copy
                    </Button>
                  </div>
                  <pre
                    className="max-h-56 overflow-auto rounded bg-muted p-3 text-xs whitespace-pre-wrap"
                    data-testid="text-support-ticket"
                  >
                    {rec.supportTicketDraft}
                  </pre>
                  <a
                    href={rec.config?.purchaseUrl ?? config?.purchaseUrl ?? "#"}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                    data-testid="link-open-maildoso-billing"
                  >
                    <ExternalLink className="h-3.5 w-3.5" /> Open Maildoso billing
                  </a>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Usage & inventory report ─────────────────────────────────────────────────

interface InventoryWorkspaceRow {
  workspaceId: string;
  workspaceName: string;
  domainsTotal: number;
  domainsLive: number;
  domainsFailed: number;
  mailboxesTotal: number;
  mailboxesLive: number;
  mailboxesFailed: number;
}

interface InventoryResponse {
  workspaces: InventoryWorkspaceRow[];
  totals: { domainsTotal: number; domainsLive: number; mailboxesTotal: number; mailboxesLive: number };
  capacity: {
    domainsCount: number;
    mailboxesUsed: number;
    mailboxesCapacity: number | null;
    mailboxesRemaining: number | null;
  } | null;
  capacityError: string | null;
}

function InventoryReportCard() {
  const { data, isLoading } = useQuery<InventoryResponse>({
    queryKey: ["/api/platform/infra/inventory"],
  });

  return (
    <Card data-testid="card-infra-inventory">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Gauge className="h-4 w-4" /> Usage &amp; inventory
        </CardTitle>
        <CardDescription>
          Per-workspace consumption and Maildoso headroom — how many mailbox slots remain across your domains before you need to buy more.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="text-inventory-loading">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading inventory…
          </div>
        ) : !data ? (
          <p className="text-sm text-muted-foreground">No inventory data.</p>
        ) : (
          <>
            {/* Capacity headroom */}
            <div className="rounded-lg border p-4">
              <div className="flex items-center gap-2 text-sm font-medium mb-2">
                <Server className="h-4 w-4" /> Maildoso capacity
              </div>
              {data.capacity ? (
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-4" data-testid="grid-capacity">
                  <div>
                    <div className="text-muted-foreground text-xs">Domains held</div>
                    <div className="font-semibold" data-testid="text-capacity-domains">{data.capacity.domainsCount}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground text-xs">Mailboxes used</div>
                    <div className="font-semibold" data-testid="text-capacity-used">{data.capacity.mailboxesUsed}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground text-xs">Capacity</div>
                    <div className="font-semibold" data-testid="text-capacity-total">
                      {data.capacity.mailboxesCapacity ?? "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground text-xs">Remaining</div>
                    <div className="font-semibold" data-testid="text-capacity-remaining">
                      {data.capacity.mailboxesRemaining ?? "—"}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground" data-testid="text-capacity-unavailable">
                  {data.capacityError ?? "Provider capacity unavailable."}
                </p>
              )}
            </div>

            {/* Per-workspace consumption */}
            {data.workspaces.length === 0 ? (
              <p className="text-sm text-muted-foreground">No workspaces have purchased sending infrastructure yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs text-muted-foreground">
                      <th className="py-2 pr-4 font-medium">Workspace</th>
                      <th className="py-2 pr-4 font-medium">Domains</th>
                      <th className="py-2 pr-4 font-medium">Mailboxes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.workspaces.map((w) => (
                      <tr key={w.workspaceId} className="border-b last:border-0" data-testid={`row-inventory-${w.workspaceId}`}>
                        <td className="py-2 pr-4 font-medium">{w.workspaceName}</td>
                        <td className="py-2 pr-4">
                          {w.domainsLive}/{w.domainsTotal} live
                          {w.domainsFailed > 0 && (
                            <span className="text-destructive"> · {w.domainsFailed} failed</span>
                          )}
                        </td>
                        <td className="py-2 pr-4">
                          {w.mailboxesLive}/{w.mailboxesTotal} live
                          {w.mailboxesFailed > 0 && (
                            <span className="text-destructive"> · {w.mailboxesFailed} failed</span>
                          )}
                        </td>
                      </tr>
                    ))}
                    <tr className="font-medium" data-testid="row-inventory-totals">
                      <td className="py-2 pr-4">All workspaces</td>
                      <td className="py-2 pr-4">{data.totals.domainsLive}/{data.totals.domainsTotal} live</td>
                      <td className="py-2 pr-4">{data.totals.mailboxesLive}/{data.totals.mailboxesTotal} live</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

interface LifecycleResponse {
  byState: Record<string, number>;
  grace: Array<{
    id: string;
    workspaceId: string;
    itemType: string;
    identifier: string;
    churnedAt: string | null;
    graceEndsAt: string | null;
    expired: boolean;
  }>;
  pendingTransferOut: Array<{
    id: string;
    workspaceId: string;
    itemType: string;
    identifier: string;
    transferOutRequestedAt: string | null;
  }>;
}

interface WarmPoolDomain {
  id: string;
  identifier: string;
  premium: boolean;
  previousWorkspaceId: string | null;
  reclaimedAt: string | null;
  health: string | null;
}

const LIFECYCLE_STATE_LABELS: Record<string, string> = {
  active: "Active",
  grace: "In grace",
  reclaiming: "Reclaiming",
  reclaimed: "Reclaimed",
  available: "Warm pool",
  retired: "Retired",
  pending_transfer_out: "Pending transfer-out",
  transferred_out: "Transferred out",
};

function LeaseLifecycleCard() {
  const { data: lifecycle, isLoading } = useQuery<LifecycleResponse>({
    queryKey: ["/api/platform/infra/lifecycle"],
  });
  const { data: warmPool } = useQuery<WarmPoolDomain[]>({
    queryKey: ["/api/platform/infra/warm-pool"],
  });

  const fmtDate = (s: string | null) =>
    s ? new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";

  const stateEntries = lifecycle
    ? Object.entries(lifecycle.byState).sort((a, b) => b[1] - a[1])
    : [];

  return (
    <Card data-testid="card-lease-lifecycle">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <RefreshCw className="h-4 w-4" /> Lease reclaim &amp; reuse
        </CardTitle>
        <CardDescription>
          Churned cold mailboxes enter a 30-day grace window (still alive for trailing replies), then get deleted at the provider so the slot returns at zero cost. Healthy reclaimed domains land in the warm pool for safe reuse.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="text-lifecycle-loading">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading lifecycle…
          </div>
        ) : !lifecycle ? (
          <p className="text-sm text-muted-foreground">No lifecycle data.</p>
        ) : (
          <>
            {/* State counts */}
            <div className="flex flex-wrap gap-2" data-testid="grid-lifecycle-states">
              {stateEntries.length === 0 ? (
                <p className="text-sm text-muted-foreground">No provisioned items yet.</p>
              ) : (
                stateEntries.map(([state, count]) => (
                  <Badge key={state} variant="secondary" data-testid={`badge-lifecycle-${state}`}>
                    {LIFECYCLE_STATE_LABELS[state] ?? state}: {count}
                  </Badge>
                ))
              )}
            </div>

            {/* Grace queue */}
            {lifecycle.grace.length > 0 && (
              <div className="rounded-lg border p-4">
                <div className="flex items-center gap-2 text-sm font-medium mb-2">
                  <Clock className="h-4 w-4" /> Grace queue ({lifecycle.grace.length})
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-xs text-muted-foreground">
                        <th className="py-2 pr-4 font-medium">Identifier</th>
                        <th className="py-2 pr-4 font-medium">Type</th>
                        <th className="py-2 pr-4 font-medium">Churned</th>
                        <th className="py-2 pr-4 font-medium">Grace ends</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lifecycle.grace.map((g) => (
                        <tr key={g.id} className="border-b last:border-0" data-testid={`row-grace-${g.id}`}>
                          <td className="py-2 pr-4 font-medium">{g.identifier}</td>
                          <td className="py-2 pr-4">{g.itemType}</td>
                          <td className="py-2 pr-4">{fmtDate(g.churnedAt)}</td>
                          <td className="py-2 pr-4">
                            {fmtDate(g.graceEndsAt)}
                            {g.expired && <span className="text-destructive"> · expired</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Pending transfer-out */}
            {lifecycle.pendingTransferOut.length > 0 && (
              <div className="rounded-lg border p-4">
                <div className="flex items-center gap-2 text-sm font-medium mb-2">
                  <ExternalLink className="h-4 w-4" /> Pending transfer-out ({lifecycle.pendingTransferOut.length})
                </div>
                <div className="space-y-1 text-sm">
                  {lifecycle.pendingTransferOut.map((t) => (
                    <div key={t.id} className="flex items-center justify-between" data-testid={`row-transfer-${t.id}`}>
                      <span className="font-medium">{t.identifier}</span>
                      <span className="text-muted-foreground text-xs">
                        {t.itemType} · requested {fmtDate(t.transferOutRequestedAt)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Warm pool */}
            <div className="rounded-lg border p-4">
              <div className="flex items-center gap-2 text-sm font-medium mb-2">
                <Server className="h-4 w-4" /> Warm reuse pool ({warmPool?.length ?? 0})
              </div>
              {!warmPool || warmPool.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="text-warm-pool-empty">
                  No domains in the warm pool. Reclaimed, healthy domains will appear here for reuse.
                </p>
              ) : (
                <div className="space-y-1 text-sm" data-testid="list-warm-pool">
                  {warmPool.map((d) => (
                    <div key={d.id} className="flex items-center justify-between" data-testid={`row-warm-${d.id}`}>
                      <span className="font-medium flex items-center gap-2">
                        {d.identifier}
                        {d.premium && <Badge variant="outline" className="text-xs">premium</Badge>}
                      </span>
                      <span className="text-muted-foreground text-xs">
                        reclaimed {fmtDate(d.reclaimedAt)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Summary endpoint types (for status strip) ────────────────────────────────

interface EmailSettingsSummary {
  relay: { liveMailboxCount: number };
  deliverability: { atRisk: number; watch: number; mailboxesPaused: number; domainsBlacklisted: number };
  sendWindow: { open: boolean; nextOpenAt: string | null; enforced: boolean; timezone: string };
}

// ─── Section type ──────────────────────────────────────────────────────────────

type EmailSection =
  | "mailboxes"
  | "infrastructure"
  | "sendwindow"
  | "deliverability"
  | "postmaster"
  | "emaillog"
  | "access";

// Maps old ?tab= values to new section ids so existing permalinks still work.
const TAB_TO_SECTION: Record<string, EmailSection> = {
  email: "mailboxes",
  infra: "infrastructure",
  "office-hours": "sendwindow",
  thresholds: "deliverability",
  monitor: "deliverability",
  log: "emaillog",
  postmaster: "postmaster",
  "gmail-read": "access",
  "outlook-requests": "access",
};

function resolveSection(raw: string | null): EmailSection {
  if (!raw) return "mailboxes";
  const mapped = TAB_TO_SECTION[raw];
  if (mapped) return mapped;
  const valid: EmailSection[] = ["mailboxes", "infrastructure", "sendwindow", "deliverability", "postmaster", "emaillog", "access"];
  return (valid as string[]).includes(raw) ? (raw as EmailSection) : "mailboxes";
}

// ─── Status strip dot ─────────────────────────────────────────────────────────

function StatusDot({ color }: { color: "green" | "amber" | "red" | "muted" }) {
  const cls = {
    green: "bg-green-500",
    amber: "bg-amber-500",
    red: "bg-red-500",
    muted: "bg-muted-foreground/40",
  }[color];
  return <span className={cn("inline-block w-2 h-2 rounded-full shrink-0", cls)} />;
}

// ─── Deliverability sub-tabs ──────────────────────────────────────────────────

type DelivSubTab = "monitor" | "rules";

// ─── Tenant Cold Inventory ────────────────────────────────────────────────────

interface TenantInvItem {
  id: string;
  purchaseId: string;
  itemType: "domain" | "mailbox";
  identifier: string;
  status: string;
  lifecycleState: string;
  coldCapable: boolean;
  managementMode: string;
  health: string | null;
}

interface TenantInvPurchase {
  id: string;
  managementMode: string;
  domainsCount: number;
  mailboxesCount: number;
  wholesaleCents: number;
  retailCents: number;
  marginCents: number;
  provisioningStatus: string;
  provider: string;
  provisionedAt: string | null;
  createdAt: string;
  notes: string | null;
  // Task #590 — true Stripe-billed monthly recurring revenue for this purchase
  // ($0 unless its subscription is currently active), plus eligibility for
  // hard-deleting a dead, never-provisioned record.
  mrrCents?: number;
  stripeSubscriptionId?: string | null;
  stripeSubscriptionStatus?: string | null;
  deletable?: boolean;
  items: TenantInvItem[];
}

interface TenantInvWorkspace {
  workspaceId: string;
  workspaceName: string;
  workspaceSlug: string;
  workspaceTier: string;
  purchases: TenantInvPurchase[];
}

interface TenantInventoryData {
  workspaces: TenantInvWorkspace[];
  summary: {
    workspaceCount: number;
    totalDomains: number;
    totalMailboxes: number;
    totalMrrCents: number;
    // True when every subscribed purchase was reconciled live against Stripe.
    mrrReconciled?: boolean;
  };
}

const fmtCentsUsd = (c: number) =>
  `$${(c / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function statusCls(s: string) {
  const m: Record<string, string> = {
    live: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
    provisioned: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
    provisioning: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
    pending_payment: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
    awaiting_capacity: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    failed: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
    cancelled: "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400",
  };
  return m[s] ?? "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400";
}

function lifecycleCls(lc: string) {
  const m: Record<string, string> = {
    active: "bg-green-100 text-green-700",
    grace: "bg-amber-100 text-amber-700",
    reclaiming: "bg-orange-100 text-orange-700",
    reclaimed: "bg-gray-100 text-gray-500",
    available: "bg-teal-100 text-teal-700",
    retired: "bg-red-100 text-red-600",
    pending_transfer_out: "bg-purple-100 text-purple-700",
    transferred_out: "bg-slate-100 text-slate-500",
  };
  return m[lc] ?? "bg-gray-100 text-gray-600";
}

function modeCls(mode: string) {
  if (mode === "lease") return "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400";
  if (mode === "buy") return "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400";
  return "bg-gray-100 text-gray-600";
}

function TenantColdInventoryCard() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [expandedWs, setExpandedWs] = useState<Set<string>>(new Set());
  const [expandedPurchases, setExpandedPurchases] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [editPurchase, setEditPurchase] = useState<TenantInvPurchase | null>(null);
  const [editRetailDollars, setEditRetailDollars] = useState("");

  const { data, isLoading, refetch } = useQuery<TenantInventoryData>({
    queryKey: ["/api/platform/infra/tenant-inventory"],
    staleTime: 30_000,
  });

  const priceMutation = useMutation({
    mutationFn: async ({ purchaseId, retailCents }: { purchaseId: string; retailCents: number }) => {
      const res = await apiRequest("PATCH", `/api/platform/infra/purchases/${purchaseId}/pricing`, { retailCents });
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/platform/infra/tenant-inventory"] });
      setEditPurchase(null);
      toast({ title: "Pricing updated" });
    },
    onError: (err: any) => {
      toast({ title: "Update failed", description: err?.message, variant: "destructive" });
    },
  });

  // Task #590 — hard-delete a dead, never-provisioned purchase row.
  const [deletePurchase, setDeletePurchase] = useState<TenantInvPurchase | null>(null);
  const deleteMutation = useMutation({
    mutationFn: async (purchaseId: string) => {
      const res = await apiRequest("DELETE", `/api/platform/infra/purchases/${purchaseId}`);
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/platform/infra/tenant-inventory"] });
      setDeletePurchase(null);
      toast({ title: "Dead purchase deleted" });
    },
    onError: (err: any) => {
      toast({ title: "Delete failed", description: err?.message, variant: "destructive" });
    },
  });

  const toggleWs = (id: string) =>
    setExpandedWs((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const togglePurchase = (id: string) =>
    setExpandedPurchases((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const openEdit = (p: TenantInvPurchase) => {
    setEditPurchase(p);
    setEditRetailDollars((p.retailCents / 100).toFixed(2));
  };

  const handlePriceSave = () => {
    if (!editPurchase) return;
    const dollars = parseFloat(editRetailDollars);
    if (isNaN(dollars) || dollars < 0) {
      toast({ title: "Invalid amount", variant: "destructive" });
      return;
    }
    priceMutation.mutate({ purchaseId: editPurchase.id, retailCents: Math.round(dollars * 100) });
  };

  const q = search.trim().toLowerCase();
  // Hide cancelled/failed purchases from the active inventory view — EXCEPT
  // dead, never-provisioned rows that are eligible for hard-delete (Task #590),
  // which we surface so an admin can clear them out.
  const INACTIVE_STATUSES = new Set(["cancelled", "failed"]);
  const filteredWorkspaces = (data?.workspaces ?? [])
    .map((ws) => ({
      ...ws,
      purchases: ws.purchases.filter(
        (p) => !INACTIVE_STATUSES.has(p.provisioningStatus) || p.deletable,
      ),
    }))
    .filter((ws) => {
      if (ws.purchases.length === 0) return false;
      if (!q) return true;
      if (ws.workspaceName.toLowerCase().includes(q) || ws.workspaceSlug.toLowerCase().includes(q)) return true;
      return ws.purchases.some((p) => p.items.some((i) => i.identifier.toLowerCase().includes(q)));
    });

  const summary = data?.summary;

  return (
    <>
      <Card data-testid="card-tenant-cold-inventory">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="h-4 w-4 text-primary" />
                Tenant Cold Infrastructure Inventory
              </CardTitle>
              <CardDescription className="mt-0.5">
                All provisioned cold domains and mailboxes across every workspace. Click a workspace to expand, then a purchase row to see individual items. Edit per-purchase retail pricing inline.
              </CardDescription>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => refetch()}
              disabled={isLoading}
              data-testid="button-refresh-inventory"
            >
              <RefreshCw className={cn("h-3.5 w-3.5", isLoading && "animate-spin")} />
            </Button>
          </div>

          {summary && (
            <div className="flex flex-wrap gap-5 mt-3 text-sm border-t pt-3">
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <Building2 className="h-3.5 w-3.5" />
                <span className="font-semibold text-foreground">{summary.workspaceCount}</span>
                workspace{summary.workspaceCount !== 1 ? "s" : ""}
              </span>
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <Globe className="h-3.5 w-3.5" />
                <span className="font-semibold text-foreground">{summary.totalDomains}</span>
                domain{summary.totalDomains !== 1 ? "s" : ""}
              </span>
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <Mail className="h-3.5 w-3.5" />
                <span className="font-semibold text-foreground">{summary.totalMailboxes}</span>
                mailbox{summary.totalMailboxes !== 1 ? "es" : ""}
              </span>
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <span className="font-semibold text-green-600">{fmtCentsUsd(summary.totalMrrCents)}/mo</span>
                active MRR
                <span
                  className="text-[10px] text-muted-foreground/70"
                  title={
                    summary.mrrReconciled === false
                      ? "Stripe was unavailable — showing stored subscription status as a fallback"
                      : "Sum of currently-active Stripe subscriptions, reconciled live against Stripe"
                  }
                >
                  {summary.mrrReconciled === false ? "(stored)" : "(Stripe-verified)"}
                </span>
              </span>
            </div>
          )}
        </CardHeader>

        <CardContent className="pt-0">
          <div className="relative mb-3">
            <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Filter by workspace name, slug, or domain…"
              className="pl-8 h-8 text-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="input-inventory-search"
            />
          </div>

          {isLoading && (
            <div className="flex items-center justify-center py-10 text-muted-foreground gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading inventory…
            </div>
          )}

          {!isLoading && filteredWorkspaces.length === 0 && (
            <p className="text-center py-10 text-sm text-muted-foreground">
              {q ? "No workspaces match that search." : "No cold infrastructure provisioned yet."}
            </p>
          )}

          <div className="space-y-2">
            {filteredWorkspaces.map((ws) => {
              const isOpen = expandedWs.has(ws.workspaceId);
              const itemsAll = ws.purchases.flatMap((p) => p.items);
              const domainCount = itemsAll.filter((i) => i.itemType === "domain").length;
              const mailboxCount = itemsAll.filter((i) => i.itemType === "mailbox").length;
              const wsMrr = ws.purchases.reduce((s, p) => s + (p.mrrCents ?? 0), 0);

              return (
                <div
                  key={ws.workspaceId}
                  className="border rounded-lg overflow-hidden"
                  data-testid={`ws-row-${ws.workspaceId}`}
                >
                  <button
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/40 transition-colors"
                    onClick={() => toggleWs(ws.workspaceId)}
                    data-testid={`button-expand-ws-${ws.workspaceId}`}
                  >
                    <ChevronRight
                      className={cn(
                        "h-4 w-4 text-muted-foreground shrink-0 transition-transform duration-150",
                        isOpen && "rotate-90"
                      )}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{ws.workspaceName}</span>
                        <span className="text-xs text-muted-foreground">@{ws.workspaceSlug}</span>
                        <Badge variant="outline" className="text-[10px] h-4 px-1.5 capitalize">
                          {ws.workspaceTier}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground shrink-0">
                      <span>{domainCount} domain{domainCount !== 1 ? "s" : ""}</span>
                      <span>{mailboxCount} mailbox{mailboxCount !== 1 ? "es" : ""}</span>
                      <span className="text-green-600 font-medium">{fmtCentsUsd(wsMrr)}/mo</span>
                    </div>
                  </button>

                  {isOpen && (
                    <div className="border-t divide-y bg-muted/20">
                      {ws.purchases.length === 0 && (
                        <p className="px-4 py-3 text-xs text-muted-foreground">No purchases found.</p>
                      )}
                      {ws.purchases.map((p) => {
                        const pDomains = p.items.filter((i) => i.itemType === "domain");
                        const pMailboxes = p.items.filter((i) => i.itemType === "mailbox");
                        const isItemsOpen = expandedPurchases.has(p.id);

                        return (
                          <div key={p.id} data-testid={`purchase-row-${p.id}`}>
                            <div className="flex items-center gap-2 px-4 py-2.5 flex-wrap min-w-0">
                              <button
                                onClick={() => togglePurchase(p.id)}
                                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground shrink-0"
                                data-testid={`button-expand-purchase-${p.id}`}
                              >
                                <ChevronRight className={cn("h-3 w-3 transition-transform duration-150", isItemsOpen && "rotate-90")} />
                                <span className="font-mono">{pDomains.length}d/{pMailboxes.length}mb</span>
                              </button>
                              <span className={cn("text-[10px] font-medium px-1.5 py-0.5 rounded shrink-0", modeCls(p.managementMode))}>
                                {p.managementMode}
                              </span>
                              <span className={cn("text-[10px] font-medium px-1.5 py-0.5 rounded shrink-0", statusCls(p.provisioningStatus))}>
                                {p.provisioningStatus.replace(/_/g, " ")}
                              </span>
                              <div className="flex items-center gap-2 text-xs ml-auto flex-wrap justify-end">
                                <span className="text-muted-foreground">{fmtCentsUsd(p.wholesaleCents)} cost</span>
                                <span className="text-muted-foreground">→</span>
                                <span className="font-medium">{fmtCentsUsd(p.retailCents)} retail</span>
                                <span className={cn("text-[11px]", p.marginCents >= 0 ? "text-green-600" : "text-red-500")}>
                                  ({p.marginCents >= 0 ? "+" : ""}{fmtCentsUsd(p.marginCents)})
                                </span>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 text-xs px-2"
                                  onClick={() => openEdit(p)}
                                  data-testid={`button-edit-pricing-${p.id}`}
                                >
                                  Edit price
                                </Button>
                                {p.deletable && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 text-xs px-2 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
                                    onClick={() => setDeletePurchase(p)}
                                    data-testid={`button-delete-purchase-${p.id}`}
                                  >
                                    <Trash2 className="h-3 w-3 mr-1" />
                                    Delete
                                  </Button>
                                )}
                              </div>
                            </div>

                            {isItemsOpen && (
                              <div className="px-4 pb-3 space-y-1">
                                {p.items.length === 0 && (
                                  <p className="text-xs text-muted-foreground">No provisioned items yet.</p>
                                )}
                                {p.items.map((item) => (
                                  <div
                                    key={item.id}
                                    className="flex items-center gap-2 text-xs py-1 border-b last:border-0"
                                    data-testid={`item-row-${item.id}`}
                                  >
                                    <span className={cn(
                                      "shrink-0 text-[10px] px-1.5 py-0.5 rounded font-medium",
                                      item.itemType === "domain"
                                        ? "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                                        : "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300"
                                    )}>
                                      {item.itemType}
                                    </span>
                                    <span className="font-mono truncate flex-1 text-[11px]">{item.identifier}</span>
                                    <span className={cn("shrink-0 text-[10px] px-1.5 py-0.5 rounded", lifecycleCls(item.lifecycleState))}>
                                      {item.lifecycleState}
                                    </span>
                                    {item.itemType === "mailbox" && (
                                      <span className={cn(
                                        "shrink-0 text-[10px] px-1.5 py-0.5 rounded",
                                        item.coldCapable
                                          ? "bg-green-100 text-green-700"
                                          : "bg-amber-100 text-amber-700"
                                      )}>
                                        {item.coldCapable ? "cold ✓" : "seasoning"}
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!editPurchase} onOpenChange={(open) => { if (!open) setEditPurchase(null); }}>
        <DialogContent className="max-w-sm" data-testid="dialog-edit-pricing">
          <DialogHeader>
            <DialogTitle>Edit Purchase Pricing</DialogTitle>
            <DialogDescription>
              Set the retail price charged to this workspace. Margin is recalculated automatically from wholesale cost.
            </DialogDescription>
          </DialogHeader>
          {editPurchase && (
            <div className="space-y-4">
              <div className="rounded-md bg-muted/60 p-3 text-sm space-y-1.5">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Wholesale (your cost)</span>
                  <span className="font-medium">{fmtCentsUsd(editPurchase.wholesaleCents)}/mo</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Current retail</span>
                  <span className="font-medium">{fmtCentsUsd(editPurchase.retailCents)}/mo</span>
                </div>
                <div className="flex justify-between border-t pt-1.5">
                  <span className="text-muted-foreground">Current margin</span>
                  <span className={cn("font-semibold", editPurchase.marginCents >= 0 ? "text-green-600" : "text-red-500")}>
                    {editPurchase.marginCents >= 0 ? "+" : ""}{fmtCentsUsd(editPurchase.marginCents)}/mo
                  </span>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">New retail price ($/mo)</Label>
                <div className="relative">
                  <span className="absolute left-2.5 top-[9px] text-muted-foreground text-sm pointer-events-none">$</span>
                  <Input
                    value={editRetailDollars}
                    onChange={(e) => setEditRetailDollars(e.target.value)}
                    className="pl-6"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    data-testid="input-retail-price"
                  />
                </div>
                {editRetailDollars && !isNaN(parseFloat(editRetailDollars)) && (
                  <p className="text-xs text-muted-foreground">
                    New margin:{" "}
                    <span className={cn(
                      "font-medium",
                      Math.round(parseFloat(editRetailDollars) * 100) - editPurchase.wholesaleCents >= 0
                        ? "text-green-600" : "text-red-500"
                    )}>
                      {fmtCentsUsd(Math.round(parseFloat(editRetailDollars) * 100) - editPurchase.wholesaleCents)}/mo
                    </span>
                  </p>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditPurchase(null)}
              data-testid="button-cancel-pricing"
            >
              Cancel
            </Button>
            <Button
              onClick={handlePriceSave}
              disabled={priceMutation.isPending}
              data-testid="button-save-pricing"
            >
              {priceMutation.isPending
                ? <Loader2 className="h-4 w-4 animate-spin mr-1" />
                : <Save className="h-4 w-4 mr-1" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deletePurchase} onOpenChange={(open) => { if (!open) setDeletePurchase(null); }}>
        <DialogContent className="max-w-sm" data-testid="dialog-delete-purchase">
          <DialogHeader>
            <DialogTitle>Delete dead purchase record?</DialogTitle>
            <DialogDescription>
              This permanently removes a purchase row that has no live, provisioned, or
              in-grace assets. It only clears the dead record — nothing is deprovisioned
              at the provider. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {deletePurchase && (
            <div className="rounded-md bg-muted/60 p-3 text-sm space-y-1.5">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Status</span>
                <span className="font-medium capitalize">{deletePurchase.provisioningStatus.replace(/_/g, " ")}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Mode</span>
                <span className="font-medium capitalize">{deletePurchase.managementMode}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Domains / Mailboxes</span>
                <span className="font-medium">{deletePurchase.domainsCount}d / {deletePurchase.mailboxesCount}mb</span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeletePurchase(null)}
              data-testid="button-cancel-delete"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deletePurchase && deleteMutation.mutate(deletePurchase.id)}
              disabled={deleteMutation.isPending}
              data-testid="button-confirm-delete"
            >
              {deleteMutation.isPending
                ? <Loader2 className="h-4 w-4 animate-spin mr-1" />
                : <Trash2 className="h-4 w-4 mr-1" />}
              Delete record
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Pool Health Dashboard ────────────────────────────────────────────────────

interface PoolCarousel {
  purchaseId: string;
  domainNames: string[];
  mailboxCount: number;
  isAvailable: boolean;
  isWarming: boolean;
  daysWarming: number | null;
  healthScore: number | null;
  healthCheckedAt: string | null;
  instantlyEmails: string[];
  lifecycleState: string | null;
  status: string;
}

interface PoolData {
  carousels: PoolCarousel[];
  available: PoolCarousel[];
  warming: PoolCarousel[];
  other: PoolCarousel[];
  total: number;
}

function PoolHealthDashboard() {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const { data, isLoading, isError, isFetching, refetch } = useQuery<PoolData>({
    queryKey: ["/api/platform/infra/pool"],
    refetchInterval: 60_000,
  });

  const carousels = data?.carousels ?? [];
  const totalDomains = carousels.reduce((s, c) => s + c.domainNames.length, 0);
  const totalMailboxes = carousels.reduce((s, c) => s + c.mailboxCount, 0);
  const scores = carousels.map((c) => c.healthScore).filter((s): s is number => s !== null);
  const avgScore = scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null;

  const scoreColor = (score: number | null) => {
    if (score === null) return "text-muted-foreground";
    if (score >= 70) return "text-green-600 dark:text-green-400";
    if (score >= 40) return "text-amber-600 dark:text-amber-400";
    return "text-red-600 dark:text-red-400";
  };

  const scoreBarClass = (score: number | null) => {
    if (score === null) return "";
    if (score >= 70) return "[&>div]:bg-green-500";
    if (score >= 40) return "[&>div]:bg-amber-500";
    return "[&>div]:bg-red-500";
  };

  return (
    <div className="space-y-4" data-testid="pool-health-dashboard">
      {/* Header row */}
      <div className="flex items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <Layers className="h-4 w-4 text-primary" />
            Carousel Pool
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Pre-warmed carousels ready to lease to tenants. Instantly.ai manages warmup automatically — carousels graduate at ≥70% health + ≥10 days.
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          onClick={() => refetch()}
          disabled={isFetching}
          data-testid="button-refresh-pool"
        >
          <RefreshCw className={cn("h-3.5 w-3.5", isFetching && "animate-spin")} />
        </Button>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          {
            label: "Ready",
            value: isLoading ? null : (data?.available.length ?? 0),
            icon: CheckCircle2,
            valueColor: "text-green-600 dark:text-green-400",
            bgColor: "bg-green-50 dark:bg-green-950/20",
            borderColor: "border-green-200 dark:border-green-800",
            desc: "cleared for cold sends",
          },
          {
            label: "Warming",
            value: isLoading ? null : (data?.warming.length ?? 0),
            icon: Flame,
            valueColor: "text-amber-600 dark:text-amber-400",
            bgColor: "bg-amber-50 dark:bg-amber-950/20",
            borderColor: "border-amber-200 dark:border-amber-800",
            desc: "in Instantly warmup",
          },
          {
            label: "Domains",
            value: isLoading ? null : totalDomains,
            icon: Globe,
            valueColor: "text-primary",
            bgColor: "bg-primary/5",
            borderColor: "border-primary/20",
            desc: "sending domains",
          },
          {
            label: "Mailboxes",
            value: isLoading ? null : totalMailboxes,
            icon: MailCheck,
            valueColor: "text-blue-600 dark:text-blue-400",
            bgColor: "bg-blue-50 dark:bg-blue-950/20",
            borderColor: "border-blue-200 dark:border-blue-800",
            desc: "active sending mailboxes",
          },
        ].map((s) => {
          const Icon = s.icon;
          return (
            <div
              key={s.label}
              className={cn("rounded-lg border p-3 space-y-1.5", s.bgColor, s.borderColor)}
              data-testid={`pool-stat-${s.label.toLowerCase()}`}
            >
              <div className="flex items-center gap-1.5">
                <Icon className={cn("h-3.5 w-3.5", s.valueColor)} />
                <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                  {s.label}
                </span>
              </div>
              {s.value === null ? (
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              ) : (
                <div className={cn("text-2xl font-bold tabular-nums", s.valueColor)}>
                  {s.value}
                </div>
              )}
              <p className="text-[10px] text-muted-foreground leading-tight">{s.desc}</p>
            </div>
          );
        })}
      </div>

      {/* Avg warmup score bar (only when there's data) */}
      {avgScore !== null && (
        <div className="rounded-lg border bg-card p-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-medium flex items-center gap-1.5 text-muted-foreground">
              <ThermometerSun className="h-3.5 w-3.5 text-amber-500" />
              Average Instantly warmup score across all carousels
            </span>
            <span className={cn("text-sm font-bold tabular-nums", scoreColor(avgScore))}>
              {avgScore}%
            </span>
          </div>
          <Progress value={avgScore} className={cn("h-2", scoreBarClass(avgScore))} />
          <p className="text-[10px] text-muted-foreground">
            {avgScore >= 70
              ? "Pool is healthy — warmed carousels are ready for cold send assignments."
              : avgScore >= 40
              ? "Warming in progress — check individual carousels below for details."
              : "Early warming stage — new mailboxes typically take 10–14 days to graduate."}
          </p>
        </div>
      )}

      {/* Carousel cards */}
      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading carousel pool…
        </div>
      ) : isError ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 flex items-center gap-2 text-sm text-destructive">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          Failed to load carousel pool. Try refreshing.
        </div>
      ) : carousels.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center space-y-2">
          <MailCheck className="h-10 w-10 text-muted-foreground mx-auto opacity-30" />
          <p className="text-sm font-medium text-muted-foreground">No carousels in pool yet</p>
          <p className="text-xs text-muted-foreground max-w-sm mx-auto">
            Use <strong>Enable cold email</strong> in the Mailboxes &amp; Relay section to provision your first carousel. Instantly.ai warmup kicks in automatically after provisioning.
          </p>
        </div>
      ) : (
        <div className="space-y-2.5" data-testid="list-carousel-pool">
          {carousels.map((c, idx) => {
            const isExpanded = expandedId === c.purchaseId;
            const borderAccent = c.isAvailable
              ? "border-green-200 dark:border-green-800"
              : c.isWarming
              ? "border-amber-200 dark:border-amber-800"
              : "";

            return (
              <div
                key={c.purchaseId}
                className={cn("rounded-lg border bg-card overflow-hidden transition-shadow hover:shadow-sm", borderAccent)}
                data-testid={`carousel-card-${c.purchaseId}`}
              >
                {/* Always-visible header row */}
                <button
                  className="w-full p-4 text-left hover:bg-muted/30 transition-colors"
                  onClick={() => setExpandedId(isExpanded ? null : c.purchaseId)}
                  data-testid={`button-expand-carousel-${c.purchaseId}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-foreground">
                          Carousel {idx + 1}
                        </span>
                        {c.isAvailable ? (
                          <Badge className="bg-green-600 hover:bg-green-700 text-white text-[10px] px-1.5 h-[18px]">
                            <CheckCircle2 className="h-2.5 w-2.5 mr-1" />
                            Ready
                          </Badge>
                        ) : c.isWarming ? (
                          <Badge variant="outline" className="text-amber-600 border-amber-300 dark:border-amber-700 text-[10px] px-1.5 h-[18px]">
                            <Flame className="h-2.5 w-2.5 mr-1" />
                            Warming{c.healthScore !== null ? ` · ${c.healthScore}%` : ""}
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="text-[10px] px-1.5 h-[18px]">
                            {c.lifecycleState ?? c.status ?? "Pending"}
                          </Badge>
                        )}
                        {c.instantlyEmails.length > 0 && (
                          <Badge variant="outline" className="text-[10px] px-1.5 h-[18px] text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-700">
                            <Wifi className="h-2.5 w-2.5 mr-1" />
                            Instantly
                          </Badge>
                        )}
                      </div>

                      <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground flex-wrap">
                        <span className="flex items-center gap-1">
                          <Globe className="h-3 w-3" />
                          {c.domainNames.slice(0, 2).join(", ")}
                          {c.domainNames.length > 2 && <span className="text-primary">+{c.domainNames.length - 2}</span>}
                        </span>
                        <span>·</span>
                        <span>{c.mailboxCount} mailbox{c.mailboxCount !== 1 ? "es" : ""}</span>
                        {c.daysWarming !== null && (
                          <>
                            <span>·</span>
                            <span>{c.daysWarming}d warmed</span>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="shrink-0 flex items-center gap-2">
                      {c.healthScore !== null && (
                        <span className={cn("text-base font-bold tabular-nums", scoreColor(c.healthScore))}>
                          {c.healthScore}%
                        </span>
                      )}
                      <ChevronRight
                        className={cn("h-4 w-4 text-muted-foreground transition-transform duration-150", isExpanded && "rotate-90")}
                      />
                    </div>
                  </div>

                  {/* Mini warmup bar — always visible when score present */}
                  {c.healthScore !== null && (
                    <div className="mt-3">
                      <Progress
                        value={c.healthScore}
                        className={cn("h-1", scoreBarClass(c.healthScore))}
                        data-testid={`warmup-bar-${c.purchaseId}`}
                      />
                    </div>
                  )}
                </button>

                {/* Expanded detail panel */}
                {isExpanded && (
                  <div className="border-t border-border bg-muted/20 px-4 py-4 space-y-4">
                    {/* Domains */}
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-2">
                        Domains ({c.domainNames.length})
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {c.domainNames.map((d) => (
                          <span
                            key={d}
                            className="inline-flex items-center gap-1 text-xs bg-background border border-border rounded-md px-2 py-1 font-mono text-foreground"
                            data-testid={`domain-chip-${d}`}
                          >
                            <Globe className="h-3 w-3 text-muted-foreground shrink-0" />
                            {d}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Mailboxes */}
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-2">
                        Mailboxes ({c.mailboxCount})
                      </p>
                      {c.instantlyEmails.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                          {c.instantlyEmails.map((email) => (
                            <span
                              key={email}
                              className="inline-flex items-center gap-1 text-xs bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 rounded-md px-2 py-1 font-mono"
                              data-testid={`mailbox-chip-${email}`}
                            >
                              <MailCheck className="h-3 w-3 shrink-0" />
                              {email}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground">
                          {c.mailboxCount} mailbox{c.mailboxCount !== 1 ? "es" : ""} provisioned — not yet enrolled in Instantly (enrollment happens automatically within 1 hour of provisioning).
                        </p>
                      )}
                    </div>

                    {/* Warmup health details */}
                    <div className="rounded-md border bg-card p-3 space-y-3">
                      <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                        Instantly Warmup Health
                      </p>
                      {c.healthScore !== null ? (
                        <>
                          <div className="flex items-center gap-3">
                            <div className="flex-1">
                              <Progress value={c.healthScore} className={cn("h-2.5", scoreBarClass(c.healthScore))} />
                            </div>
                            <span className={cn("text-lg font-bold tabular-nums w-12 text-right", scoreColor(c.healthScore))}>
                              {c.healthScore}%
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-3 text-xs">
                            <div className="space-y-0.5">
                              <p className="text-muted-foreground">Days warming</p>
                              <p className="font-medium">{c.daysWarming !== null ? `${c.daysWarming} day${c.daysWarming !== 1 ? "s" : ""}` : "—"}</p>
                            </div>
                            <div className="space-y-0.5">
                              <p className="text-muted-foreground">Last checked</p>
                              <p className="font-medium">
                                {c.healthCheckedAt
                                  ? new Date(c.healthCheckedAt).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
                                  : "—"}
                              </p>
                            </div>
                            <div className="space-y-0.5">
                              <p className="text-muted-foreground">Graduate threshold</p>
                              <p className={cn("font-medium", c.healthScore >= 70 && c.daysWarming !== null && c.daysWarming >= 10 ? "text-green-600 dark:text-green-400" : "text-muted-foreground")}>
                                {c.healthScore >= 70 && c.daysWarming !== null && c.daysWarming >= 10
                                  ? "✓ Met — ready to graduate"
                                  : `Needs ≥70% score + ≥10 days`}
                              </p>
                            </div>
                            <div className="space-y-0.5">
                              <p className="text-muted-foreground">Instantly enrolled</p>
                              <p className="font-medium">{c.instantlyEmails.length > 0 ? `${c.instantlyEmails.length} mailbox${c.instantlyEmails.length !== 1 ? "es" : ""}` : "Pending enrollment"}</p>
                            </div>
                          </div>
                        </>
                      ) : c.instantlyEmails.length > 0 ? (
                        <p className="text-xs text-muted-foreground">
                          Enrolled in Instantly. First health score will appear within the next hourly poll.
                        </p>
                      ) : (
                        <p className="text-xs text-muted-foreground">
                          Not yet enrolled. The warmup scheduler runs every hour and will enroll this carousel automatically.
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Main EmailSettingsContent ────────────────────────────────────────────────

export function EmailSettingsContent() {
  const { toast } = useToast();

  // ── Active section ────────────────────────────────────────────────────────
  const [activeSection, setActiveSection] = useState<EmailSection>(() => {
    if (typeof window === "undefined") return "mailboxes";
    const params = new URLSearchParams(window.location.search);
    return resolveSection(params.get("tab") || params.get("section"));
  });
  const [delivSubTab, setDelivSubTab] = useState<DelivSubTab>("monitor");
  const [infraSubTab, setInfraSubTab] = useState<"pool" | "inventory" | "config" | "store">("pool");

  // ── Handle Postmaster OAuth return ────────────────────────────────────────
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const pm = params.get("postmaster");
    if (pm === "success") {
      setActiveSection("postmaster");
      toast({ title: "Postmaster connected", description: "Centralized Postmaster is now linked." });
    } else if (pm === "error") {
      toast({ title: "Postmaster connection failed", description: params.get("reason") || "Unknown error", variant: "destructive" });
    }
    if (pm) {
      params.delete("postmaster");
      params.delete("reason");
      params.delete("postmasterScope");
      const qs = params.toString();
      window.history.replaceState({}, "", `${window.location.pathname}${qs ? `?${qs}` : ""}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Status strip data ─────────────────────────────────────────────────────
  const { data: summary } = useQuery<EmailSettingsSummary>({
    queryKey: ["/api/platform/email-settings/summary"],
    staleTime: 60_000,
  });

  const { data: gmailOverview } = useQuery<{
    seats: { remainingOfCap: number; totalConsumed: number; totalCap: number };
    funnel: { requestsPending: number };
  }>({
    queryKey: ["/api/platform/gmail-read/overview"],
    staleTime: 60_000,
  });

  const pendingAccessCount = gmailOverview?.funnel?.requestsPending ?? 0;

  // ── Nav groups ────────────────────────────────────────────────────────────
  const navGroups: { label: string; items: { id: EmailSection; label: string; icon: React.ElementType; badge?: number }[] }[] = [
    {
      label: "Setup",
      items: [
        { id: "mailboxes", label: "Mailboxes & Relay", icon: Mail },
        { id: "infrastructure", label: "Sending Infrastructure", icon: Server },
        { id: "sendwindow", label: "Send Window", icon: Clock },
      ],
    },
    {
      label: "Health",
      items: [
        { id: "deliverability", label: "Deliverability", icon: Activity },
        { id: "postmaster", label: "Postmaster", icon: ShieldCheck },
      ],
    },
    {
      label: "Activity",
      items: [{ id: "emaillog", label: "Email Log", icon: Search }],
    },
    {
      label: "Access",
      items: [{ id: "access", label: "Mailbox Access", icon: Key, badge: pendingAccessCount > 0 ? pendingAccessCount : undefined }],
    },
  ];

  const navigate = (section: EmailSection) => setActiveSection(section);

  // ── Status strip helpers ──────────────────────────────────────────────────
  const relayLive = summary?.relay?.liveMailboxCount ?? 0;
  const delivAtRisk = summary?.deliverability?.atRisk ?? 0;
  const delivWatch = summary?.deliverability?.watch ?? 0;
  const delivPaused = summary?.deliverability?.mailboxesPaused ?? 0;
  const delivBlacklisted = summary?.deliverability?.domainsBlacklisted ?? 0;
  const windowOpen = summary?.sendWindow?.open ?? false;
  const nextOpenAt = summary?.sendWindow?.nextOpenAt ?? null;
  const gmailRemaining = gmailOverview?.seats?.remainingOfCap ?? null;
  const gmailConsumed = gmailOverview?.seats?.totalConsumed ?? null;
  const gmailCap = gmailOverview?.seats?.totalCap ?? 100;

  const fmtResume = (iso: string | null) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleString(undefined, {
        month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit",
        timeZoneName: "short",
      });
    } catch { return null; }
  };

  return (
    <div className="space-y-4" data-testid="page-platform-email-settings">
      {/* Page header */}
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <Mail className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold">Email Settings</h1>
          <p className="text-sm text-muted-foreground">
            Connect mailboxes, configure the cold-email relay, and keep sending healthy across every workspace.
          </p>
        </div>
      </div>

      {/* ── Status strip ── */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="email-status-strip">
        {/* Relay */}
        <button
          onClick={() => navigate("mailboxes")}
          className="rounded-lg border border-border bg-card p-3 text-left hover:border-primary/50 transition-colors"
          data-testid="status-card-relay"
        >
          <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1.5">Relay</div>
          <div className="flex items-center gap-2 font-semibold text-sm">
            {!summary ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
            ) : relayLive > 0 ? (
              <><StatusDot color="green" />{relayLive} live mailbox{relayLive !== 1 ? "es" : ""}</>
            ) : (
              <><StatusDot color="amber" />Not configured</>
            )}
          </div>
          <div className="text-[11px] text-muted-foreground mt-1">
            {relayLive > 0 ? "Cold sends active" : "Cold sends are blocked until set up"}
          </div>
        </button>

        {/* Deliverability */}
        <button
          onClick={() => navigate("deliverability")}
          className="rounded-lg border border-border bg-card p-3 text-left hover:border-primary/50 transition-colors"
          data-testid="status-card-deliverability"
        >
          <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1.5">Deliverability</div>
          <div className="flex items-center gap-2 font-semibold text-sm">
            {!summary ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
            ) : delivAtRisk > 0 ? (
              <><StatusDot color="red" />{delivAtRisk} at risk</>
            ) : delivWatch > 0 ? (
              <><StatusDot color="amber" />{delivWatch} watching</>
            ) : (
              <><StatusDot color="green" />Healthy</>
            )}
          </div>
          <div className="text-[11px] text-muted-foreground mt-1">
            {delivAtRisk} at risk · {delivPaused} paused · {delivBlacklisted} blacklisted
          </div>
        </button>

        {/* Send window */}
        <button
          onClick={() => navigate("sendwindow")}
          className="rounded-lg border border-border bg-card p-3 text-left hover:border-primary/50 transition-colors"
          data-testid="status-card-sendwindow"
        >
          <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1.5">Send window</div>
          <div className="flex items-center gap-2 font-semibold text-sm">
            {!summary ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
            ) : windowOpen ? (
              <><StatusDot color="green" />Open</>
            ) : (
              <><StatusDot color="red" />Closed</>
            )}
          </div>
          <div className="text-[11px] text-muted-foreground mt-1">
            {!windowOpen && nextOpenAt ? `Resumes ${fmtResume(nextOpenAt) ?? nextOpenAt}` : windowOpen ? "Automated sends active" : "No send window configured"}
          </div>
        </button>

        {/* Gmail read seats */}
        <button
          onClick={() => navigate("access")}
          className="rounded-lg border border-border bg-card p-3 text-left hover:border-primary/50 transition-colors"
          data-testid="status-card-gmail-seats"
        >
          <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1.5">Gmail read seats</div>
          <div className="flex items-center gap-2 font-semibold text-sm">
            {gmailRemaining === null ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
            ) : (
              <>{gmailRemaining} left</>
            )}
          </div>
          <div className="text-[11px] text-muted-foreground mt-1">
            {gmailConsumed !== null ? `${gmailConsumed} of ${gmailCap} lifetime cap used` : "Loading…"}
          </div>
        </button>
      </div>

      {/* ── Layout: side nav + content ── */}
      <div className="flex gap-6 items-start">
        {/* Side nav — desktop sticky */}
        <nav className="hidden md:block w-52 shrink-0 sticky top-4 space-y-4" data-testid="email-settings-sidenav">
          {navGroups.map((group) => (
            <div key={group.label}>
              <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground px-3 pb-1.5">
                {group.label}
              </div>
              {group.items.map((item) => {
                const Icon = item.icon;
                const active = activeSection === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => navigate(item.id)}
                    data-testid={`nav-item-${item.id}`}
                    className={cn(
                      "flex items-center gap-2.5 w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors mb-0.5",
                      active
                        ? "bg-primary text-primary-foreground"
                        : "hover:bg-muted text-foreground",
                    )}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="flex-1">{item.label}</span>
                    {item.badge ? (
                      <Badge
                        className={cn(
                          "text-[10px] h-4.5 px-1.5 rounded-full",
                          active ? "bg-white/20 text-white border-0" : "bg-amber-100 text-amber-700 border-0",
                        )}
                        data-testid={`badge-nav-${item.id}`}
                      >
                        {item.badge}
                      </Badge>
                    ) : null}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Mobile: horizontal scroll nav */}
        <div className="md:hidden w-full overflow-x-auto flex gap-2 pb-2 border-b border-border" data-testid="email-settings-mobile-nav">
          {navGroups.flatMap((g) =>
            g.items.map((item) => {
              const Icon = item.icon;
              const active = activeSection === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => navigate(item.id)}
                  className={cn(
                    "flex items-center gap-1.5 shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors",
                    active
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background text-foreground border-border hover:border-primary/50",
                  )}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {item.label}
                  {item.badge ? (
                    <span className="ml-1 bg-amber-500 text-white rounded-full text-[10px] px-1">{item.badge}</span>
                  ) : null}
                </button>
              );
            }),
          )}
        </div>

        {/* ── Section content ── */}
        <div className="flex-1 min-w-0 space-y-6">

          {/* MAILBOXES & RELAY */}
          {activeSection === "mailboxes" && (
            <div className="space-y-6" data-testid="section-mailboxes">
              {/* Connected / warm mailboxes */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                        <LinkIcon className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-base">Warm mailboxes</CardTitle>
                        <CardDescription>
                          Used for 1:1 warm sends from the Email Hub. Cold bulk outreach never uses this path.
                        </CardDescription>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <PlatformGmailConnector returnUrl="/platform/email-settings" />
                </CardContent>
              </Card>

              {/* Platform Cold Email (Maildoso) */}
              <PlatformColdEmailCard />
            </div>
          )}

          {/* SENDING INFRASTRUCTURE */}
          {activeSection === "infrastructure" && (
            <div className="space-y-4" data-testid="section-infrastructure">
              {/* Sub-tab segmented control */}
              <div className="flex gap-1 bg-muted rounded-lg p-1 w-fit flex-wrap" data-testid="infra-subtabs">
                {(
                  [
                    { id: "pool", label: "Pool Health", icon: Layers },
                    { id: "inventory", label: "Inventory", icon: BarChart3 },
                    { id: "config", label: "Config", icon: Settings2 },
                    { id: "store", label: "Store", icon: Package },
                  ] as const
                ).map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    onClick={() => setInfraSubTab(id)}
                    data-testid={`infra-subtab-${id}`}
                    className={cn(
                      "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
                      infraSubTab === id
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {label}
                  </button>
                ))}
              </div>

              {/* Pool Health — carousel dashboard + legacy placement-test warming */}
              {infraSubTab === "pool" && (
                <div className="space-y-6">
                  <PoolHealthDashboard />
                  <SeasoningStatusCard />
                </div>
              )}

              {/* Inventory — cross-tenant view, lease lifecycle, domain requests */}
              {infraSubTab === "inventory" && (
                <div className="space-y-6">
                  <TenantColdInventoryCard />
                  <InventoryReportCard />
                  <LeaseLifecycleCard />
                  <ColdDomainRequestsCard />
                </div>
              )}

              {/* Config — markup, capacity buffer */}
              {infraSubTab === "config" && (
                <div className="space-y-6">
                  <MarkupConfigCard />
                  <CapacityBufferCard />
                </div>
              )}

              {/* Store — packages + all orders (no duplicate pool panel) */}
              {infraSubTab === "store" && (
                <InfraStorefront surface="platform" />
              )}
            </div>
          )}

          {/* SEND WINDOW (was: Office Hours) */}
          {activeSection === "sendwindow" && (
            <div data-testid="section-sendwindow">
              <PlatformOfficeHoursPanel />
            </div>
          )}

          {/* DELIVERABILITY (Monitor + Pause rules merged) */}
          {activeSection === "deliverability" && (
            <div className="space-y-4" data-testid="section-deliverability">
              {/* Sub-tab segmented control */}
              <div className="flex gap-1 bg-muted rounded-md p-1 w-fit" data-testid="deliverability-subtabs">
                <button
                  onClick={() => setDelivSubTab("monitor")}
                  data-testid="subtab-monitor"
                  className={cn(
                    "px-4 py-1.5 rounded text-sm font-medium transition-colors",
                    delivSubTab === "monitor"
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  Monitor
                </button>
                <button
                  onClick={() => setDelivSubTab("rules")}
                  data-testid="subtab-pause-rules"
                  className={cn(
                    "px-4 py-1.5 rounded text-sm font-medium transition-colors",
                    delivSubTab === "rules"
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  Pause rules
                </button>
              </div>

              {delivSubTab === "monitor" ? (
                <DeliverabilityMonitorCard />
              ) : (
                <DeliverabilityThresholdsCard />
              )}
            </div>
          )}

          {/* POSTMASTER */}
          {activeSection === "postmaster" && (
            <div data-testid="section-postmaster">
              <CentralizedPostmasterCard />
            </div>
          )}

          {/* EMAIL LOG */}
          {activeSection === "emaillog" && (
            <div data-testid="section-emaillog">
              <EmailLogCard />
            </div>
          )}

          {/* MAILBOX ACCESS (Gmail Read + Outlook Requests merged) */}
          {activeSection === "access" && (
            <div className="space-y-6" data-testid="section-access">
              <GmailReadContent />
              <OutlookRequestsCard />
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

// ─── Outlook Requests log (Task #388) ────────────────────────────────────────
// Read-only demand-signal log: who asked for Outlook / Office 365 / Hotmail
// support. Passive — no approve/reject/appeal workflow.

interface OutlookRequestRow {
  id: string;
  workspaceId: string;
  workspaceName: string | null;
  userId: string;
  userEmail: string;
  reason: string | null;
  createdAt: string;
}

function OutlookRequestsCard() {
  const { data, isLoading } = useQuery<{ requests: OutlookRequestRow[]; total: number }>({
    queryKey: ["/api/platform/outlook/requests"],
  });
  const requests = data?.requests ?? [];

  return (
    <Card data-testid="card-outlook-requests">
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-primary" />
              Outlook Support Requests
            </CardTitle>
            <CardDescription>
              Users who've asked for Outlook / Office 365 / Hotmail support. Demand signal only — no
              integration is connected.
            </CardDescription>
          </div>
          <Badge variant="secondary" data-testid="badge-outlook-total">{data?.total ?? 0} total</Badge>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading requests…
          </div>
        ) : requests.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6" data-testid="text-outlook-empty">
            No Outlook support requests yet.
          </p>
        ) : (
          <div className="space-y-2">
            {requests.map((r) => (
              <div
                key={r.id}
                className="flex flex-col gap-1 rounded-md border border-border p-3 sm:flex-row sm:items-start sm:justify-between"
                data-testid={`row-outlook-request-${r.id}`}
              >
                <div className="min-w-0 space-y-0.5">
                  <p className="text-sm font-medium truncate" data-testid={`text-outlook-email-${r.id}`}>
                    {r.userEmail}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {r.workspaceName ?? "Unknown workspace"}{" "}
                    <span className="font-mono opacity-70">({r.workspaceId})</span>
                  </p>
                  {r.reason ? (
                    <p className="text-sm text-muted-foreground mt-1" data-testid={`text-outlook-reason-${r.id}`}>
                      {r.reason}
                    </p>
                  ) : null}
                </div>
                <span className="shrink-0 text-xs text-muted-foreground" data-testid={`text-outlook-date-${r.id}`}>
                  {new Date(r.createdAt).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Deliverability Thresholds (Task #394) ───────────────────────────────────
// Platform-wide defaults every workspace INHERITS. A per-workspace/per-mailbox
// settings row is an OVERRIDE; blank here means "use hardcoded fallback".

type ThresholdDefaults = {
  bounceThreshold24h: number;
  bounceThreshold7d: number;
  optOutThreshold7d: number;
  midflightPauseBounceRate: number;
  watchBounceRate24h: number;
  watchBounceRate7d: number;
  watchSpamComplaintRate: number;
  watchOptOutRate7d: number;
  watchOpenRateFloor: number;
};

const THRESHOLD_FIELDS: {
  key: keyof ThresholdDefaults;
  label: string;
  hint: string;
  group: "mailbox" | "watch";
}[] = [
  { key: "bounceThreshold24h", label: "Bounce rate (24h) auto-pause", hint: "Pause a mailbox when its 24-hour bounce rate exceeds this.", group: "mailbox" },
  { key: "bounceThreshold7d", label: "Bounce rate (7d) auto-pause", hint: "Pause a mailbox when its 7-day bounce rate exceeds this.", group: "mailbox" },
  { key: "optOutThreshold7d", label: "Opt-out rate (7d) auto-pause", hint: "Pause a mailbox when its 7-day opt-out rate exceeds this.", group: "mailbox" },
  { key: "midflightPauseBounceRate", label: "Mid-flight bounce pause", hint: "Pause an in-progress campaign when bounces exceed this (spec floor still applies).", group: "mailbox" },
  { key: "watchBounceRate24h", label: "Watch: bounce rate (24h)", hint: "Raise an early-warning alert below the auto-pause line.", group: "watch" },
  { key: "watchBounceRate7d", label: "Watch: bounce rate (7d)", hint: "Raise an early-warning alert below the auto-pause line.", group: "watch" },
  { key: "watchSpamComplaintRate", label: "Watch: spam complaint rate", hint: "Raise an alert when spam complaints exceed this.", group: "watch" },
  { key: "watchOptOutRate7d", label: "Watch: opt-out rate (7d)", hint: "Raise an alert when opt-outs exceed this.", group: "watch" },
  { key: "watchOpenRateFloor", label: "Watch: open-rate floor", hint: "Raise an alert when the open rate drops below this floor.", group: "watch" },
];

function DeliverabilityThresholdsCard() {
  const { toast } = useToast();
  const { data, isLoading } = useQuery<{ defaults: ThresholdDefaults; hardcodedFallbacks: ThresholdDefaults }>({
    queryKey: ["/api/platform/email-settings/thresholds"],
  });
  // Stored as fractions (0.05); edited as percentages (5).
  const [draft, setDraft] = useState<Record<string, string>>({});

  useEffect(() => {
    if (data?.defaults) {
      const d: Record<string, string> = {};
      for (const f of THRESHOLD_FIELDS) {
        d[f.key] = String(+(data.defaults[f.key] * 100).toFixed(4));
      }
      setDraft(d);
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: Record<string, number | null> = {};
      for (const f of THRESHOLD_FIELDS) {
        const raw = draft[f.key];
        if (raw === "" || raw == null) {
          payload[f.key] = null;
        } else {
          payload[f.key] = Number(raw) / 100;
        }
      }
      return (await apiRequest("PUT", "/api/platform/email-settings/thresholds", payload)).json();
    },
    onSuccess: () => {
      globalQueryClient.invalidateQueries({ queryKey: ["/api/platform/email-settings/thresholds"] });
      toast({ title: "Thresholds saved", description: "New defaults apply to every workspace without an override." });
    },
    onError: (e: any) => toast({ title: "Error", description: e?.message || "Failed to save thresholds", variant: "destructive" }),
  });

  const renderField = (f: (typeof THRESHOLD_FIELDS)[number]) => {
    const fallbackPct = data ? +(data.hardcodedFallbacks[f.key] * 100).toFixed(4) : 0;
    return (
      <div key={f.key} className="space-y-1.5">
        <Label htmlFor={`threshold-${f.key}`}>{f.label}</Label>
        <div className="flex items-center gap-2">
          <Input
            id={`threshold-${f.key}`}
            data-testid={`input-threshold-${f.key}`}
            type="number"
            step="0.01"
            min={0}
            max={100}
            className="w-28"
            placeholder={`${fallbackPct}`}
            value={draft[f.key] ?? ""}
            onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
          />
          <span className="text-sm text-muted-foreground">%</span>
        </div>
        <p className="text-[11px] text-muted-foreground">{f.hint} Fallback: {fallbackPct}%.</p>
      </div>
    );
  };

  return (
    <Card data-testid="card-thresholds">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <SlidersHorizontal className="h-4 w-4" /> Pause rules
        </CardTitle>
        <CardDescription>
          Platform-wide defaults every workspace <strong>inherits</strong>. A workspace or mailbox with its
          own setting <strong>overrides</strong> these. Leave a field blank to fall back to the built-in
          safety value. Values are percentages (e.g. 5 = 5%).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading rules…
          </div>
        ) : (
          <>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-3">Auto-pause</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {THRESHOLD_FIELDS.filter((f) => f.group === "mailbox").map(renderField)}
              </div>
            </div>
            <Separator />
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-3">Early warning</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {THRESHOLD_FIELDS.filter((f) => f.group === "watch").map(renderField)}
              </div>
            </div>
            <Button
              size="sm"
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending}
              data-testid="button-save-thresholds"
            >
              {saveMutation.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
              Save rules
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Cross-tenant Deliverability Monitor (Task #394) ─────────────────────────

interface MonitorAlertDetail {
  alertType: string;
  severity: string;
  message: string;
  domain: string | null;
  mailboxId: string | null;
}

interface MonitorRow {
  workspaceId: string;
  workspaceName: string;
  warmAlertsAtRisk: number;
  warmAlertsWatch: number;
  warmAlertDetails: MonitorAlertDetail[];
  mailboxesPaused: number;
  reputationAlerts: number;
  dmarcAlerts: number;
  coldDomains: number;
  coldDomainsUnhealthy: number;
  coldDomainsBlacklisted: number;
  coldMailboxes: number;
  coldMailboxesUnhealthy: number;
  health: "healthy" | "watch" | "atRisk";
}

interface MonitorResponse {
  workspaces: MonitorRow[];
  totals: {
    workspaces: number;
    atRisk: number;
    watch: number;
    healthy: number;
    coldDomainsBlacklisted: number;
    mailboxesPaused: number;
  };
}

function HealthBadge({ health }: { health: MonitorRow["health"] }) {
  if (health === "atRisk")
    return <Badge variant="destructive" data-testid={`badge-health-${health}`}><ShieldAlert className="mr-1 h-3 w-3" /> At risk</Badge>;
  if (health === "watch")
    return <Badge variant="secondary" data-testid={`badge-health-${health}`}><AlertTriangle className="mr-1 h-3 w-3" /> Watch</Badge>;
  return <Badge variant="outline" data-testid={`badge-health-${health}`}><CheckCircle2 className="mr-1 h-3 w-3 text-green-600" /> Healthy</Badge>;
}

function DeliverabilityMonitorCard() {
  const { data, isLoading, refetch, isFetching } = useQuery<MonitorResponse>({
    queryKey: ["/api/platform/email-oversight/monitor"],
  });

  return (
    <Card data-testid="card-monitor">
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="h-4 w-4" /> Cross-tenant deliverability monitor
            </CardTitle>
            <CardDescription>
              Warm (in-app alerts) and cold (sending-infra health) signals across every workspace.
              Worst-first so problems float to the top.
            </CardDescription>
          </div>
          <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching} data-testid="button-refresh-monitor">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${isFetching ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading monitor…
          </div>
        ) : !data ? (
          <p className="text-sm text-muted-foreground">No monitor data.</p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">At risk</div>
                <div className="text-xl font-semibold text-destructive" data-testid="text-monitor-atrisk">{data.totals.atRisk}</div>
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">Watch</div>
                <div className="text-xl font-semibold" data-testid="text-monitor-watch">{data.totals.watch}</div>
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">Mailboxes paused</div>
                <div className="text-xl font-semibold" data-testid="text-monitor-paused">{data.totals.mailboxesPaused}</div>
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">Domains blacklisted</div>
                <div className="text-xl font-semibold" data-testid="text-monitor-blacklisted">{data.totals.coldDomainsBlacklisted}</div>
              </div>
            </div>

            {data.workspaces.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="text-monitor-empty">
                No email activity across any workspace yet.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs text-muted-foreground">
                      <th className="py-2 pr-4 font-medium">Workspace</th>
                      <th className="py-2 pr-4 font-medium">Health</th>
                      <th className="py-2 pr-4 font-medium">Warm alerts</th>
                      <th className="py-2 pr-4 font-medium">Paused</th>
                      <th className="py-2 pr-4 font-medium">Cold domains</th>
                      <th className="py-2 pr-4 font-medium">Cold mailboxes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.workspaces.map((w) => (
                      <tr key={w.workspaceId} className="border-b last:border-0" data-testid={`row-monitor-${w.workspaceId}`}>
                        <td className="py-2 pr-4 font-medium">{w.workspaceName}</td>
                        <td className="py-2 pr-4"><HealthBadge health={w.health} /></td>
                        <td className="py-2 pr-4">
                          {(w.warmAlertsAtRisk > 0 || w.warmAlertsWatch > 0) ? (
                            <Popover>
                              <PopoverTrigger asChild>
                                <button
                                  className="text-left hover:underline focus:outline-none"
                                  data-testid={`button-warm-alerts-${w.workspaceId}`}
                                >
                                  {w.warmAlertsAtRisk > 0 && (
                                    <span className="text-destructive">{w.warmAlertsAtRisk} at-risk</span>
                                  )}
                                  {w.warmAlertsAtRisk > 0 && w.warmAlertsWatch > 0 && <span className="text-muted-foreground mx-1">·</span>}
                                  {w.warmAlertsWatch > 0 && (
                                    <span className="text-amber-600">{w.warmAlertsWatch} watch</span>
                                  )}
                                </button>
                              </PopoverTrigger>
                              <PopoverContent className="w-80 p-3 space-y-2" align="start" data-testid={`popover-warm-alerts-${w.workspaceId}`}>
                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Active alerts</p>
                                {(w.warmAlertDetails ?? []).map((a, i) => (
                                  <div key={i} className="flex items-start gap-2">
                                    {a.severity === "atRisk"
                                      ? <ShieldAlert className="h-3.5 w-3.5 text-destructive mt-0.5 shrink-0" />
                                      : <AlertTriangle className="h-3.5 w-3.5 text-amber-500 mt-0.5 shrink-0" />
                                    }
                                    <div className="min-w-0">
                                      <p className="text-xs leading-snug">{a.message}</p>
                                      {a.domain && <p className="text-[11px] text-muted-foreground mt-0.5">{a.domain}</p>}
                                    </div>
                                  </div>
                                ))}
                              </PopoverContent>
                            </Popover>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="py-2 pr-4">{w.mailboxesPaused || <span className="text-muted-foreground">—</span>}</td>
                        <td className="py-2 pr-4">
                          {w.coldDomains}
                          {w.coldDomainsBlacklisted > 0 && <span className="text-destructive"> · {w.coldDomainsBlacklisted} blacklisted</span>}
                          {w.coldDomainsUnhealthy > 0 && <span className="text-amber-600"> · {w.coldDomainsUnhealthy} unhealthy</span>}
                        </td>
                        <td className="py-2 pr-4">
                          {w.coldMailboxes}
                          {w.coldMailboxesUnhealthy > 0 && <span className="text-amber-600"> · {w.coldMailboxesUnhealthy} unhealthy</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Cross-tenant Email Log (Task #394) ──────────────────────────────────────

interface EmailLogRow {
  id: string;
  workspaceId: string | null;
  workspaceName: string | null;
  recipientEmail: string;
  recipientType: string;
  subject: string | null;
  status: string;
  sentAt: string | null;
}

interface EmailLogResponse {
  rows: EmailLogRow[];
  total: number;
}

function EmailLogCard() {
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [workspaceFilter, setWorkspaceFilter] = useState("all");
  const [sortBy, setSortBy] = useState<"sentAt" | "recipientEmail" | "status" | "workspaceName">("sentAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(0);
  const [exporting, setExporting] = useState(false);
  const limit = 100;

  // Debounce the search box so typing doesn't fire a query per keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(0); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  // Workspace list for the filter dropdown.
  const { data: workspacesList } = useQuery<{ id: string; name: string }[]>({
    queryKey: ["/api/platform/workspaces"],
  });

  const buildParams = (overrides?: Record<string, string>) => {
    const p = new URLSearchParams();
    if (debouncedSearch) p.set("search", debouncedSearch);
    if (statusFilter !== "all") p.set("status", statusFilter);
    if (workspaceFilter !== "all") p.set("workspaceId", workspaceFilter);
    p.set("sortBy", sortBy);
    p.set("sortDir", sortDir);
    p.set("limit", String(limit));
    p.set("offset", String(page * limit));
    if (overrides) Object.entries(overrides).forEach(([k, v]) => p.set(k, v));
    return p;
  };

  const { data, isLoading, isFetching } = useQuery<EmailLogResponse>({
    queryKey: ["/api/platform/email-oversight/log", debouncedSearch, statusFilter, workspaceFilter, sortBy, sortDir, page],
    queryFn: async () => (await apiRequest("GET", `/api/platform/email-oversight/log?${buildParams().toString()}`)).json(),
  });

  const toggleSort = (col: typeof sortBy) => {
    if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(col); setSortDir("desc"); }
    setPage(0);
  };

  const SortHeader = ({ col, children }: { col: typeof sortBy; children: React.ReactNode }) => (
    <th className="py-2 pr-4 font-medium">
      <button
        className="inline-flex items-center gap-1 hover:text-foreground"
        onClick={() => toggleSort(col)}
        data-testid={`sort-${col}`}
      >
        {children}
        <SlidersHorizontal className={`h-3 w-3 ${sortBy === col ? "opacity-100" : "opacity-30"}`} />
      </button>
    </th>
  );

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const exportParams = buildParams({ limit: "1000", offset: "0" });
      const res = await apiRequest("GET", `/api/platform/email-oversight/log?${exportParams.toString()}`);
      const json: EmailLogResponse = await res.json();
      const rows = json.rows ?? [];
      if (rows.length === 0) {
        toast({ title: "Nothing to export", description: "No rows match the current filters." });
        return;
      }
      const header = ["Sent At", "Workspace", "Recipient", "Subject", "Status"];
      const lines = [
        header.join(","),
        ...rows.map((r) =>
          [
            r.sentAt ? new Date(r.sentAt).toISOString() : "",
            `"${(r.workspaceName || r.workspaceId || "").replace(/"/g, '""')}"`,
            `"${r.recipientEmail.replace(/"/g, '""')}"`,
            `"${(r.subject || "").replace(/"/g, '""')}"`,
            r.status,
          ].join(","),
        ),
      ];
      const blob = new Blob([lines.join("\n")], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `email-log-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      toast({ title: "Export failed", description: err?.message, variant: "destructive" });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card data-testid="card-email-log">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Search className="h-4 w-4" /> Cross-tenant email log
            </CardTitle>
            <CardDescription>
              Every outbound email across all workspaces — for support triage. Filter by workspace,
              status, or search recipient / subject. Hover a subject to see the full text.
            </CardDescription>
          </div>
          <Button
            size="sm"
            variant="outline"
            disabled={exporting || isLoading}
            onClick={handleExportCsv}
            data-testid="button-export-csv"
          >
            {exporting ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}
            Export CSV
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="log-search">Search</Label>
            <Input
              id="log-search"
              data-testid="input-log-search"
              placeholder="recipient or subject…"
              className="w-56"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Workspace</Label>
            <Select value={workspaceFilter} onValueChange={(v) => { setWorkspaceFilter(v); setPage(0); }}>
              <SelectTrigger className="w-44" data-testid="select-log-workspace"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All workspaces</SelectItem>
                {(workspacesList ?? []).map((ws) => (
                  <SelectItem key={ws.id} value={ws.id}>{ws.name || ws.id}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Status</Label>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
              <SelectTrigger className="w-40" data-testid="select-log-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="skipped">Skipped</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Badge variant="secondary" data-testid="badge-log-total">{total.toLocaleString()} total</Badge>
        </div>

        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading log…
          </div>
        ) : !data || data.rows.length === 0 ? (
          <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="text-log-empty">
            No emails match these filters. Every outbound email across all workspaces will appear here once sending begins.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <SortHeader col="sentAt">Sent</SortHeader>
                    <SortHeader col="workspaceName">Workspace</SortHeader>
                    <SortHeader col="recipientEmail">Recipient</SortHeader>
                    <th className="py-2 pr-4 font-medium">Subject</th>
                    <SortHeader col="status">Status</SortHeader>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.id} className="border-b last:border-0 hover:bg-muted/30" data-testid={`row-log-${r.id}`}>
                      <td className="py-2 pr-4 whitespace-nowrap text-xs text-muted-foreground">
                        {r.sentAt ? new Date(r.sentAt).toLocaleString() : "—"}
                      </td>
                      <td className="py-2 pr-4">{r.workspaceName || <span className="font-mono text-xs opacity-70">{r.workspaceId}</span>}</td>
                      <td className="py-2 pr-4 max-w-[180px] truncate">{r.recipientEmail}</td>
                      <td className="py-2 pr-4 max-w-xs">
                        <span className="truncate block" title={r.subject || undefined}>
                          {r.subject || <span className="text-muted-foreground">—</span>}
                        </span>
                      </td>
                      <td className="py-2 pr-4">
                        {r.status === "sent" ? (
                          <Badge variant="outline" className="border-green-200 bg-green-50 text-green-700 dark:bg-green-950/30 dark:text-green-400"><CheckCircle2 className="mr-1 h-3 w-3" /> Sent</Badge>
                        ) : r.status === "failed" ? (
                          <Badge variant="destructive">Failed</Badge>
                        ) : r.status === "skipped" ? (
                          <Badge variant="secondary">Skipped</Badge>
                        ) : (
                          <Badge variant="secondary">{r.status}</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground" data-testid="text-log-page">
                Page {page + 1} of {totalPages}
              </span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={page === 0 || isFetching} onClick={() => setPage((p) => Math.max(0, p - 1))} data-testid="button-log-prev">
                  Previous
                </Button>
                <Button size="sm" variant="outline" disabled={page + 1 >= totalPages || isFetching} onClick={() => setPage((p) => p + 1)} data-testid="button-log-next">
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Centralized Postmaster (Task #394) ──────────────────────────────────────
// Clarik-controlled (leased/bought) cold sending domains. Maildoso has NO
// DNS-write API, so enrollment is a documented one-time per-domain DNS step —
// not automatic. See docs/centralized-postmaster.md.

interface PostmasterDomainSnapshot {
  domain: string;
  verified: boolean;
  reputation: string | null;
  spamRate: number | null;
  spfPassRate: number | null;
  dkimPassRate: number | null;
  dmarcPassRate: number | null;
  dataDate: string | null;
  error?: string;
}

interface PostmasterStatus {
  configured: boolean;
  testStub: boolean;
  connected: boolean;
  accountEmail: string | null;
  connectedAt: string | null;
  lastSyncedAt: string | null;
  lastSyncError: string | null;
  domains: string[];
  cachedDomains: PostmasterDomainSnapshot[] | null;
  walkthrough: { title?: string; steps?: string[] } | null;
  autoEnrollAvailable: boolean;
}

function CentralizedPostmasterCard() {
  const { toast } = useToast();
  const { data, isLoading } = useQuery<PostmasterStatus>({
    queryKey: ["/api/platform/email-oversight/postmaster"],
  });

  const connectMutation = useMutation({
    mutationFn: async () => (await apiRequest("POST", "/api/platform/email-oversight/postmaster/connect", {})).json(),
    onSuccess: (res: { authUrl: string }) => { if (res.authUrl) window.location.href = res.authUrl; },
    onError: (e: any) => toast({ title: "Error", description: e?.message || "Failed to start connection", variant: "destructive" }),
  });
  const disconnectMutation = useMutation({
    mutationFn: async () => (await apiRequest("POST", "/api/platform/email-oversight/postmaster/disconnect", {})).json(),
    onSuccess: () => { globalQueryClient.invalidateQueries({ queryKey: ["/api/platform/email-oversight/postmaster"] }); toast({ title: "Disconnected" }); },
    onError: (e: any) => toast({ title: "Error", description: e?.message, variant: "destructive" }),
  });
  const syncMutation = useMutation({
    mutationFn: async () => (await apiRequest("POST", "/api/platform/email-oversight/postmaster/sync", {})).json(),
    onSuccess: () => { globalQueryClient.invalidateQueries({ queryKey: ["/api/platform/email-oversight/postmaster"] }); toast({ title: "Synced", description: "Latest Postmaster snapshots fetched." }); },
    onError: (e: any) => toast({ title: "Sync failed", description: e?.message, variant: "destructive" }),
  });

  const fmtPct = (v: number | null) => (v == null ? "—" : `${(v * 100).toFixed(2)}%`);

  return (
    <div className="space-y-6">
      <Card data-testid="card-postmaster">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" /> Centralized Postmaster
          </CardTitle>
          <CardDescription>
            Google Postmaster reputation for Clarik-controlled (leased/bought) cold sending domains.
            One platform connection covers them all.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading Postmaster status…
            </div>
          ) : !data ? (
            <p className="text-sm text-muted-foreground">No status available.</p>
          ) : (
            <>
              {!data.configured && (
                <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-3 text-sm" data-testid="text-postmaster-unconfigured">
                  Google OAuth credentials are not configured. Set <code>GOOGLE_CLIENT_ID</code> and{" "}
                  <code>GOOGLE_CLIENT_SECRET</code> to enable Postmaster.
                </div>
              )}

              <div className="flex items-center justify-between rounded-lg border p-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium flex items-center gap-2">
                    {data.connected ? (
                      <Badge variant="outline" data-testid="badge-postmaster-connected"><CheckCircle2 className="mr-1 h-3 w-3 text-green-600" /> Connected</Badge>
                    ) : (
                      <Badge variant="secondary" data-testid="badge-postmaster-disconnected">Not connected</Badge>
                    )}
                    {data.testStub && <Badge variant="secondary">test stub</Badge>}
                    {data.accountEmail && <span className="text-xs text-muted-foreground truncate">{data.accountEmail}</span>}
                  </div>
                  {data.connectedAt && (
                    <p className="text-[11px] text-muted-foreground mt-1">Connected {new Date(data.connectedAt).toLocaleString()}</p>
                  )}
                  {data.lastSyncedAt && (
                    <p className="text-[11px] text-muted-foreground">Last synced {new Date(data.lastSyncedAt).toLocaleString()}</p>
                  )}
                  {data.lastSyncError && (
                    <p className="text-[11px] text-destructive">Last sync error: {data.lastSyncError}</p>
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  {data.connected ? (
                    <>
                      <Button size="sm" variant="outline" onClick={() => syncMutation.mutate()} disabled={syncMutation.isPending} data-testid="button-postmaster-sync">
                        {syncMutation.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                        Sync
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => disconnectMutation.mutate()} disabled={disconnectMutation.isPending} data-testid="button-postmaster-disconnect">
                        Disconnect
                      </Button>
                    </>
                  ) : (
                    <Button size="sm" onClick={() => connectMutation.mutate()} disabled={connectMutation.isPending || !data.configured} data-testid="button-postmaster-connect">
                      {connectMutation.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Plug className="h-3.5 w-3.5 mr-1.5" />}
                      Connect Postmaster
                    </Button>
                  )}
                </div>
              </div>

              {/* Domain snapshots */}
              {data.domains.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="text-postmaster-no-domains">
                  No Clarik-controlled domains enrolled yet. Domains are picked up automatically from
                  Maildoso-provisioned sending infrastructure. Provision at least one domain in the
                  Sending Infrastructure section, or add domains manually via the PM_DOMAINS override
                  in platform settings.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-xs text-muted-foreground">
                        <th className="py-2 pr-4 font-medium">Domain</th>
                        <th className="py-2 pr-4 font-medium">Verified</th>
                        <th className="py-2 pr-4 font-medium">Reputation</th>
                        <th className="py-2 pr-4 font-medium">Spam</th>
                        <th className="py-2 pr-4 font-medium">SPF</th>
                        <th className="py-2 pr-4 font-medium">DKIM</th>
                        <th className="py-2 pr-4 font-medium">DMARC</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.domains.map((domain) => {
                        const snap = data.cachedDomains?.find((s) => s.domain === domain);
                        return (
                          <tr key={domain} className="border-b last:border-0" data-testid={`row-postmaster-${domain}`}>
                            <td className="py-2 pr-4 font-mono text-xs">{domain}</td>
                            <td className="py-2 pr-4">
                              {!snap ? <span className="text-muted-foreground">—</span> : snap.verified ? (
                                <Badge variant="outline"><CheckCircle2 className="mr-1 h-3 w-3 text-green-600" /> Yes</Badge>
                              ) : (
                                <Badge variant="secondary">Not verified</Badge>
                              )}
                            </td>
                            <td className="py-2 pr-4">{snap?.reputation ?? "—"}</td>
                            <td className="py-2 pr-4">{snap ? fmtPct(snap.spamRate) : "—"}</td>
                            <td className="py-2 pr-4">{snap ? fmtPct(snap.spfPassRate) : "—"}</td>
                            <td className="py-2 pr-4">{snap ? fmtPct(snap.dkimPassRate) : "—"}</td>
                            <td className="py-2 pr-4">{snap ? fmtPct(snap.dmarcPassRate) : "—"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* One-time DNS enrollment walkthrough — Maildoso can't write DNS for us. */}
      <Card data-testid="card-postmaster-enrollment">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" /> One-time domain enrollment (manual)
          </CardTitle>
          <CardDescription>
            Maildoso has no DNS-write API, so auto-enrollment is impossible. Each Clarik-controlled
            domain must be verified in Google Postmaster once, by hand.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <ol className="list-decimal space-y-1.5 pl-5 text-muted-foreground" data-testid="list-enrollment-steps">
            <li>Open Google Postmaster Tools and add each domain listed above.</li>
            <li>Postmaster shows a TXT verification record for the domain.</li>
            <li>Add that TXT record at the domain's DNS host (the registrar / DNS provider for the leased domain — Maildoso cannot do this for you).</li>
            <li>Wait for DNS propagation, then click "Verify" in Postmaster.</li>
            <li>Return here and press <strong>Sync</strong> to pull the first reputation snapshot (data appears after a day or two of volume).</li>
          </ol>
          <a
            href="https://postmaster.google.com/managedomains"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-primary hover:underline"
            data-testid="link-postmaster-tools"
          >
            <ExternalLink className="h-3.5 w-3.5" /> Open Google Postmaster Tools
          </a>
          <p className="text-[11px] text-muted-foreground">
            Full runbook: <code>docs/centralized-postmaster.md</code>.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

export default function PlatformEmailSettingsPage() {
  return (
    <PlatformLayout>
      <EmailSettingsContent />
    </PlatformLayout>
  );
}
